import React, { useState, useEffect, useRef, useContext, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  TextField,
  CircularProgress,
  Alert,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  Divider,
} from "@mui/material";
import { Icon } from "../../icons";
import { COLORS, TYPOGRAPHY } from "../../constants/color";
import { useAuth } from "../../context/authContext";
import axios from "axios";
import { url } from "../../constants/url";
import PhotoUploadWithDrawing from "./PhotoUploadWithDrawing";
import SignatureCanvas from "./SignatureCanvas";
import AppToast from "../AppToast";
import AssignEngineerModal from "./AssignEngineer";

// Create Toast Context
const ToastContext = React.createContext();
export const useToast = () => useContext(ToastContext);

const STATUS_STEPS = [
  "Unassigned",
  "Assigned",
  "Visited",
  "Sign Pending",
  "Final Report",
];

// Status badge color mapping
const STATUS_COLORS = {
  UNASSIGNED: "#F8D66D",
  ASSIGNED: "#FFB54C",
  VISITED: "#B3EBF2",
  SIGNPENDING: "#A5E3C2",
  FINALREPORT: "#8CD47E",
  REJECTED: "#FF6961",
};

// Helper function to get full image URL
const getFullImageUrl = (imagePath) => {
  if (!imagePath) return null;

  if (imagePath.startsWith("http://") || imagePath.startsWith("https://")) {
    return imagePath;
  }

  if (imagePath.startsWith("data:image")) {
    return imagePath;
  }

  const cleanPath = imagePath.startsWith("/")
    ? imagePath.substring(1)
    : imagePath;
  return `${url}/${cleanPath}`;
};

// NEW: Helper function to check if current date matches assigned visit date
const isAssignedVisitDate = (assignedVisitDate) => {
  if (!assignedVisitDate) return false;

  const assignedDate = new Date(assignedVisitDate);
  const today = new Date();

  // Compare only year, month, and day (ignore time)
  const assignedDateOnly = new Date(
    assignedDate.getFullYear(),
    assignedDate.getMonth(),
    assignedDate.getDate()
  );

  const todayOnly = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate()
  );

  return assignedDateOnly.getTime() === todayOnly.getTime();
};

// NEW: Function to get edit permission based on date
const getEditPermission = (userRole, projectStatus, assignedVisitDate, editable = true) => {
  // Only ENGINEER and ADMIN/SUPERADMIN can edit
  if (!["ENGINEER", "ADMIN", "SUPERADMIN"].includes(userRole)) {
    return false;
  }

  // Check if the component is editable (mode = "edit")
  if (!editable) return false;

  // Check project status - only editable for certain statuses
  const editableStatuses = ["ASSIGNED", "VISITED", "SIGNPENDING"];
  if (!editableStatuses.includes(projectStatus)) {
    return false;
  }

  // For BOTH ENGINEER and ADMIN: only editable on assigned visit date
  return isAssignedVisitDate(assignedVisitDate);
};

// Divider Line
const DividerLine = () => (
  <Box sx={{ borderTop: `1px solid ${COLORS.LIGHT_SECONDARY}`, my: 2 }} />
);

// StatusProgress Component
const StatusProgress = ({ currentStatus, userRole }) => {
  const getFilteredSteps = () => {
    if (userRole === "CLIENT") {
      return STATUS_STEPS.filter((step) => step !== "Sign Pending");
    }
    return STATUS_STEPS;
  };

  const filteredSteps = getFilteredSteps();

  const getActiveIndex = (status) => {
    const statusMap = {
      UNASSIGNED: 0,
      ASSIGNED: 1,
      VISITED: userRole === "CLIENT" ? 2 : 2,
      SIGNPENDING: userRole === "CLIENT" ? 2 : 3,
      FINALREPORT: userRole === "CLIENT" ? 3 : 4,
      REJECTED: -1,
    };
    return statusMap[status] || 0;
  };

  const ACTIVE_INDEX = getActiveIndex(currentStatus);
  const DOT_SIZE = 14;
  const DOT_BORDER = 2;
  const STEP_GAP = userRole === "CLIENT" ? 115 : 80;
  const DOT_CENTER = (DOT_SIZE + DOT_BORDER * 2) / 2;

  return (
    <Box sx={{ py: 3 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          px: 3,
          position: "relative",
        }}
      >
        {filteredSteps.map((label, index) => {
          const originalIndex = STATUS_STEPS.indexOf(label);
          const isActive = originalIndex <= ACTIVE_INDEX;
          const isCompleted = originalIndex < ACTIVE_INDEX;

          return (
            <Box
              key={label}
              sx={{
                width: STEP_GAP,
                textAlign: "center",
                position: "relative",
              }}
            >
              <Box
                sx={{
                  width: DOT_SIZE,
                  height: DOT_SIZE,
                  borderRadius: "50%",
                  bgcolor: isActive ? COLORS.SECONDARY : "#fff",
                  border: `${DOT_BORDER}px solid ${COLORS.SECONDARY}`,
                  mx: "auto",
                  position: "relative",
                  zIndex: 2,
                }}
              />

              {index !== filteredSteps.length - 1 && (
                <Box
                  sx={{
                    position: "absolute",
                    top: DOT_CENTER - 1,
                    left: `calc(50% + ${DOT_SIZE / 2}px)`,
                    width: STEP_GAP + DOT_SIZE * 5,
                    height: 2,
                    bgcolor: isCompleted
                      ? COLORS.SECONDARY
                      : COLORS.LIGHT_SECONDARY,
                    zIndex: 1,
                  }}
                />
              )}

              <Typography
                sx={{
                  ...TYPOGRAPHY.CAPTION_MICRO,
                  color: COLORS.PRIMARY,
                  mt: 1,
                  whiteSpace: "nowrap",
                }}
              >
                {label}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

// Edit Date Modal Component
const EditDateModal = ({
  open,
  onClose,
  dateEdits,
  setDateEdits,
  onSave,
  loading,
}) => {
  const formatDateForInput = (date) => {
    if (!date) return "";
    const d = new Date(date);
    d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
    return d.toISOString().slice(0, 10);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Icon name="calendarEdit" color={COLORS.PRIMARY} size={20} />
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.PRIMARY }}>
              Edit Preferred Visit Date
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small" disabled={loading}>
            <Icon name="close" color={COLORS.ERROR} size={18} />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3, mt: 2 }}>
          <Box>
            <Typography
              sx={{
                ...TYPOGRAPHY.BODY_SMALL,
                color: COLORS.PRIMARY,
                mb: 1,
                fontWeight: 600,
              }}
            >
              From Date *
            </Typography>
            <TextField
              type="date"
              value={formatDateForInput(dateEdits.fromDate)}
              onChange={(e) => {
                const newDate = e.target.value
                  ? new Date(e.target.value)
                  : null;
                setDateEdits((prev) => ({ ...prev, fromDate: newDate }));
              }}
              fullWidth
              size="small"
              sx={{
                "& .MuiOutlinedInput-root": {
                  "& fieldset": { borderColor: COLORS.LIGHT_SECONDARY },
                  "&:hover fieldset": { borderColor: COLORS.SECONDARY },
                },
              }}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Box>

          <Box>
            <Typography
              sx={{
                ...TYPOGRAPHY.BODY_SMALL,
                color: COLORS.PRIMARY,
                mb: 1,
                fontWeight: 600,
              }}
            >
              To Date *
            </Typography>
            <TextField
              type="date"
              value={formatDateForInput(dateEdits.toDate)}
              onChange={(e) => {
                const newDate = e.target.value
                  ? new Date(e.target.value)
                  : null;
                setDateEdits((prev) => ({ ...prev, toDate: newDate }));
              }}
              fullWidth
              size="small"
              sx={{
                "& .MuiOutlinedInput-root": {
                  "& fieldset": { borderColor: COLORS.LIGHT_SECONDARY },
                  "&:hover fieldset": { borderColor: COLORS.SECONDARY },
                },
              }}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Box>

          {/* <Typography sx={{ ...TYPOGRAPHY.BODY_SMALL, color: COLORS.TEXT, fontStyle: 'italic', mt: 1 }}>
                        Note: You can only edit the preferred visit date for requests that are not yet assigned.
                    </Typography> */}
        </Box>
      </DialogContent>
      <Divider
        sx={{ borderTop: `1px solid ${COLORS.LIGHT_SECONDARY}}`, my: 2 }}
      />
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          onClick={onClose}
          disabled={loading}
          startIcon={<Icon name="close2" size={18} />}
          sx={{
            color: COLORS.ERROR,
            border: `1px solid ${COLORS.ERROR}`,
            //borderColor: COLORS.LIGHT_SECONDARY,
            "&:hover": { bgcolor: "rgba(0,0,0,0.04)" },
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={onSave}
          startIcon={<Icon name="check" size={18} />}
          disabled={loading || !dateEdits.fromDate || !dateEdits.toDate}
          sx={{
            bgcolor: COLORS.SECONDARY,
            color: "#fff",
            "&:hover": { bgcolor: COLORS.SECONDARY },
            "&:disabled": { bgcolor: COLORS.LIGHT_SECONDARY },
          }}
        >
          {loading ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            "Save Changes"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Edit Additional Time Modal Component
const EditAdditionalTimeModal = ({
  open,
  onClose,
  additionalTime,
  setAdditionalTime,
  onSave,
  loading,
}) => {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {/* <Icon name="edit" color={COLORS.PRIMARY} size={20} /> */}
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.PRIMARY }}>
              Edit Additional Instructions
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small" disabled={loading}>
            <Icon name="close" color={COLORS.ERROR} size={18} />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Typography
            sx={{
              ...TYPOGRAPHY.BODY_SMALL,
              color: COLORS.PRIMARY,
              mb: 1,
              fontWeight: 600,
            }}
          >
            Additional Time / Instructions
          </Typography>
          <TextField
            value={additionalTime}
            onChange={(e) => setAdditionalTime(e.target.value)}
            placeholder="Enter any additional time preferences or instructions"
            fullWidth
            multiline
            rows={3}
            size="small"
            sx={{
              "& .MuiOutlinedInput-root": {
                "& fieldset": { borderColor: COLORS.LIGHT_SECONDARY },
                "&:hover fieldset": { borderColor: COLORS.SECONDARY },
                "&.Mui-focused fieldset": { borderColor: COLORS.SECONDARY },
              },
              "& .MuiOutlinedInput-input": {
                resize: "vertical",
                minHeight: "60px",
                maxHeight: "120px",
                overflow: "auto !important",
                "&::-webkit-scrollbar": {
                  display: "none",
                },
                "-ms-overflow-style": "none",
                "scrollbar-width": "none",
              },
            }}
          />
        </Box>
      </DialogContent>
      <Divider
        sx={{ borderTop: `1px solid ${COLORS.LIGHT_SECONDARY}}`, my: 2 }}
      />
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          onClick={onClose}
          disabled={loading}
          startIcon={<Icon name="close2" color={COLORS.ERROR} />}
          sx={{
            border: `1px solid ${COLORS.ERROR}`,

            color: COLORS.ERROR,
            "&:hover": { bgcolor: "rgba(0,0,0,0.04)" },
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={onSave}
          disabled={loading}
          startIcon={<Icon name="check" size={18} />}
          sx={{
            bgcolor: COLORS.SECONDARY,
            color: "#fff",
            "&:hover": { bgcolor: COLORS.SECONDARY },
          }}
        >
          {loading ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            "Save Changes"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Drawing Viewer Modal Component
const DrawingViewerModal = ({ open, onClose, drawing }) => {
  if (!drawing) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen
      PaperProps={{
        sx: {
          m: 0,
          height: "100vh",
          width: "100vw",
          maxHeight: "100vh",
          maxWidth: "100vw",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
          pb: 1,
          bgcolor: "#fff",
          position: "sticky",
          top: 0,
          zIndex: 1,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Drawing {drawing.drawingNumber || "1"}
        </Typography>
        <IconButton onClick={onClose} size="small">
          <Icon name="close" color={COLORS.TEXT} />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          p: 0,
          display: "flex",
          flexDirection: "column",
          height: "calc(100vh - 64px)",
          bgcolor: "#f5f5f5",
        }}
      >
        {drawing.fullUrl && (
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              height: "100%",
              width: "100%",
            }}
          >
            {drawing.fullUrl.toLowerCase().endsWith(".pdf") ? (
              <Box
                sx={{
                  flex: 1,
                  height: "100%",
                  width: "100%",
                  border: "none",
                }}
              >
                <iframe
                  src={`${drawing.fullUrl}#view=FitH`}
                  title={`Drawing ${drawing.drawingNumber}`}
                  style={{
                    width: "100%",
                    height: "100%",
                    border: "none",
                  }}
                />
              </Box>
            ) : (
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  overflow: "auto",
                  p: 2,
                  width: "100%",
                }}
              >
                <Box
                  component="img"
                  src={drawing.fullUrl}
                  alt={`Drawing ${drawing.drawingNumber}`}
                  sx={{
                    maxWidth: "100%",
                    maxHeight: "100%",
                    objectFit: "contain",
                  }}
                />
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          borderTop: `1px solid ${COLORS.LIGHT_SECONDARY}`,
          p: 1.5,
          bgcolor: "#fff",
          position: "sticky",
          bottom: 0,
          zIndex: 1,
        }}
      >
        <Button
          onClick={onClose}
          sx={{
            color: COLORS.PRIMARY,
            textTransform: "none",
            fontWeight: 600,
          }}
        >
          Close
        </Button>
        <Button
          onClick={() => window.open(drawing.fullUrl, "_blank")}
          startIcon={<Icon name="external" size={16} />}
          sx={{
            bgcolor: COLORS.PRIMARY,
            color: "#fff",
            textTransform: "none",
            fontWeight: 600,
            "&:hover": {
              bgcolor: "#1976d2",
            },
          }}
        >
          Open in New Tab
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const ProjectMetaRow = ({
  project,
  role,
  onAssignEngineer,
  onDateUpdate,
  onAdditionalTimeUpdate,
  editable = true,
  onOpenDrawing,
}) => {
  const [editDateModalOpen, setEditDateModalOpen] = useState(false);
  const [editAdditionalTimeModalOpen, setEditAdditionalTimeModalOpen] =
    useState(false);
  const [dateEdits, setDateEdits] = useState({
    fromDate: null,
    toDate: null,
  });
  const [additionalTimeEdit, setAdditionalTimeEdit] = useState("");

  // State variables for immediate display
  const [displayDateRange, setDisplayDateRange] = useState("");
  const [displayAdditionalTime, setDisplayAdditionalTime] = useState("");
  const [isDataReady, setIsDataReady] = useState(false);

  // Add ref to track if component is mounted
  const isMounted = useRef(true);

  // Initialize display states
  useEffect(() => {
    isMounted.current = true;

    if (!project) {
      if (isMounted.current) {
        setIsDataReady(false);
      }
      return;
    }

    const updateInitialDisplay = () => {
      if (!isMounted.current) return;

      // Set display date range
      if (project?.preferredSiteVisitDate?.fromDate) {
        const fromDate = new Date(project.preferredSiteVisitDate.fromDate);
        const toDate = project.preferredSiteVisitDate.toDate
          ? new Date(project.preferredSiteVisitDate.toDate)
          : null;

        const format = (date) => {
          return date.toLocaleDateString("en-IN", {
            day: "numeric",
            month: "short",
            year: "numeric",
          });
        };

        let formattedDate = "";
        if (toDate && fromDate.toDateString() !== toDate.toDateString()) {
          formattedDate = `${format(fromDate)} - ${format(toDate)}`;
        } else {
          formattedDate = format(fromDate);
        }

        setDisplayDateRange(formattedDate);
        setDateEdits({
          fromDate: fromDate,
          toDate: toDate,
        });
      } else {
        setDisplayDateRange("Not specified");
        setDateEdits({
          fromDate: null,
          toDate: null,
        });
      }

      // Set additional time
      if (project?.additionalTime) {
        setDisplayAdditionalTime(project.additionalTime);
        setAdditionalTimeEdit(project.additionalTime);
      } else {
        setDisplayAdditionalTime("Not specified");
        setAdditionalTimeEdit("");
      }

      if (isMounted.current) {
        setIsDataReady(true);
      }
    };

    updateInitialDisplay();

    return () => {
      isMounted.current = false;
    };
  }, [project]);

  // Helper function to format and update display date range
  const updateDisplayDateRange = useCallback((dateRange) => {
    if (!isMounted.current) return;

    if (!dateRange?.fromDate) {
      setDisplayDateRange("Not specified");
      return;
    }

    const fromDate = new Date(dateRange.fromDate);
    const toDate = dateRange.toDate ? new Date(dateRange.toDate) : null;

    const format = (date) => {
      return date.toLocaleDateString("en-IN", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
    };

    let formattedDate = "Not specified";
    if (toDate && fromDate.toDateString() !== toDate.toDateString()) {
      formattedDate = `${format(fromDate)} - ${format(toDate)}`;
    } else {
      formattedDate = format(fromDate);
    }

    setDisplayDateRange(formattedDate);
  }, []);

  const handleOpenDateEdit = useCallback((e) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }

    if (!isMounted.current) return;

    if (project?.preferredSiteVisitDate) {
      setDateEdits({
        fromDate: new Date(project.preferredSiteVisitDate.fromDate),
        toDate: project.preferredSiteVisitDate.toDate
          ? new Date(project.preferredSiteVisitDate.toDate)
          : null,
      });
    }
    setEditDateModalOpen(true);
  }, [project?.preferredSiteVisitDate]);

  const handleOpenAdditionalTimeEdit = useCallback((e) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }

    if (!isMounted.current) return;

    setAdditionalTimeEdit(project?.additionalTime || "");
    setEditAdditionalTimeModalOpen(true);
  }, [project?.additionalTime]);

  const handleDateSave = useCallback(() => {
    if (!isMounted.current) return;

    if (onDateUpdate && dateEdits.fromDate && dateEdits.toDate) {
      onDateUpdate({
        fromDate: dateEdits.fromDate,
        toDate: dateEdits.toDate,
      });

      updateDisplayDateRange({
        fromDate: dateEdits.fromDate,
        toDate: dateEdits.toDate,
      });

      setEditDateModalOpen(false);
    }
  }, [dateEdits, onDateUpdate, updateDisplayDateRange]);

  const handleAdditionalTimeSave = useCallback(() => {
    if (!isMounted.current) return;

    if (onAdditionalTimeUpdate && additionalTimeEdit !== undefined) {
      onAdditionalTimeUpdate(additionalTimeEdit);

      setDisplayAdditionalTime(additionalTimeEdit || "Not specified");
      setEditAdditionalTimeModalOpen(false);
    }
  }, [additionalTimeEdit, onAdditionalTimeUpdate]);

  const canEdit = useCallback(() => {
    return (
      (role === "ADMIN" || role === "SUPERADMIN") &&
      project?.status &&
      editable
    );
  }, [role, project?.status, editable]);

  if (!isDataReady) {
    return (
      <Box sx={{ height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  const hasDrawings = project?.specificInstructions?.ImageTrackList?.length > 0;
  const firstDrawing = hasDrawings
    ? project.specificInstructions.ImageTrackList[0]
    : null;

  const hasAdminActionButton =
    (role === "ADMIN" || role === "SUPERADMIN") &&
    project?.status &&
    ["UNASSIGNED", "ASSIGNED"].includes(project.status);

  const hasClientActionButton =
    (role === "ADMIN" ||
      role === "SUPERADMIN" ||
      role === "CLIENT" ||
      role === "ENGINEER") &&
    project?.status === "FINALREPORT";

  const hasActionButton = hasAdminActionButton || hasClientActionButton;
  const showDrawingSection = hasDrawings && firstDrawing;

  return (
    <>
      {/* Titles Row - Using Flexbox with proper alignment */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          mb: 0.5,
          width: "100%",
        }}
      >
        {/* Site Checks Title */}
        <Box sx={{
          flex: "0 0 auto",
          display: "flex",
          alignItems: "center",
          mr: 2,
          width: "120px"
        }}>
          <Typography sx={{
            ...TYPOGRAPHY.BODY_SMALL,
            color: COLORS.TEXT,
            whiteSpace: "nowrap"
          }}>
            Site Checks:
          </Typography>
        </Box>

        {/* Preferred Date Title */}
        <Box sx={{
          flex: "0 0 auto",
          display: "flex",
          alignItems: "center",
          gap: 1,
          mr: 2,
          width: "180px"
        }}>
          <Typography sx={{
            ...TYPOGRAPHY.BODY_SMALL,
            color: COLORS.TEXT,
            whiteSpace: "nowrap"
          }}>
            Preferred Date
          </Typography>
          {canEdit() && (
            <IconButton
              size="small"
              onClick={handleOpenDateEdit}
              sx={{
                padding: 0.5,
                flexShrink: 0,
                "&:hover": {
                  backgroundColor: "rgba(0,0,0,0.04)",
                  transform: "scale(1.1)",
                },
              }}
            >
              <Icon name="edit" size={16} color={COLORS.SECONDARY} />
            </IconButton>
          )}
        </Box>

        {/* Additional Time Title - Moved left by reducing width */}
        <Box sx={{
          flex: "0 0 auto",
          display: "flex",
          alignItems: "center",
          gap: 1,
          width: "220px", // Reduced from 240px to move left
          mr: 2, // Added margin right
        }}>
          <Typography sx={{
            ...TYPOGRAPHY.BODY_SMALL,
            color: COLORS.TEXT,
            whiteSpace: "nowrap",
          }}>
            Additional Time / Instructions:
          </Typography>
          {canEdit() && (
            <IconButton
              size="small"
              onClick={handleOpenAdditionalTimeEdit}
              sx={{
                padding: 0.5,
                flexShrink: 0,
                "&:hover": {
                  backgroundColor: "rgba(0,0,0,0.04)",
                  transform: "scale(1.1)",
                },
              }}
            >
              <Icon name="edit" size={16} color={COLORS.SECONDARY} />
            </IconButton>
          )}
        </Box>

        {/* Empty space for button column */}
        <Box sx={{
          flex: "0 0 auto",
          width: "180px"
        }} />
      </Box>

      {/* Content Row - Using Flexbox with proper alignment */}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start", // Keep as flex-start for variable height
          mb: hasDrawings ? 2 : 0,
          width: "100%",
        }}
      >
        {/* Site Checks Content */}
        <Box
          sx={{
            flex: "0 0 auto",
            ...TYPOGRAPHY.BODY_STANDARD,
            color: COLORS.SECONDARY,
            whiteSpace: "normal",
            wordBreak: "break-word",
            p: 1,
            borderRadius: 1,
            minHeight: 40,
            display: "flex",
            alignItems: "flex-start", // Align to top
            mr: 2,
            width: "120px",
          }}
        >
          {project?.templateTitle || "N/A"}
        </Box>

        {/* Preferred Date Content */}
        <Box
          sx={{
            flex: "0 0 auto",
            ...TYPOGRAPHY.BODY_STANDARD,
            color: COLORS.SECONDARY,
            whiteSpace: "normal",
            wordBreak: "break-word",
            bgcolor: "rgba(0,0,0,0.02)",
            p: 1,
            borderRadius: 1,
            border: `1px solid ${COLORS.LIGHT_SECONDARY}`,
            minHeight: 40,
            display: "flex",
            alignItems: "flex-start", // Align to top
            mr: 2,
            width: "180px",
          }}
        >
          {displayDateRange}
        </Box>

        {/* Additional Time Content - Reduced width and added margin */}
        <Box
          sx={{
            flex: "0 0 auto",
            ...TYPOGRAPHY.BODY_STANDARD,
            color: COLORS.SECONDARY,
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            bgcolor: "rgba(0,0,0,0.02)",
            p: 1,
            borderRadius: 1,
            border: `1px solid ${COLORS.LIGHT_SECONDARY}`,
            minHeight: 40,
            width: "200px", // Reduced from 220px
            minWidth: "200px",
            maxWidth: "200px",
            overflow: "auto",
            maxHeight: "110px",
            display: "flex",
            alignItems: "flex-start", // Align to top
            mr: 2, // Added margin right
          }}
        >
          {displayAdditionalTime}
        </Box>

        {/* Action Buttons Column - FIXED: Proper vertical alignment */}
        <Box
          sx={{
            flex: "0 0 auto",
            width: "180px",
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "flex-start", // Align to top
            minHeight: "40px", // Match content min-height
          }}
        >
          {hasAdminActionButton && (
            <Button
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                onAssignEngineer();
              }}
              startIcon={
                project.status === "ASSIGNED" ? (
                  <Icon name="edit" color="#fff" size={18} />
                ) : (
                  <Icon name="plus2" color="#fff" size={18} />
                )
              }
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
                bgcolor:
                  project.status === "ASSIGNED"
                    ? COLORS.PRIMARY
                    : COLORS.SECONDARY,
                color: "#fff",
                px: 2,
                py: 1.2,
                borderRadius: 2,
                cursor: "pointer",
                fontWeight: 600,
                whiteSpace: "nowrap",
                textTransform: "none",
                height: "40px",
                minWidth: "180px",
                "&:hover": {
                  bgcolor:
                    project.status === "ASSIGNED"
                      ? COLORS.PRIMARY_DARK
                      : COLORS.SECONDARY_DARK,
                },
              }}
            >
              {project.status === "ASSIGNED"
                ? "Edit Engineer"
                : "Assign Engineer"}
            </Button>
          )}

          {!hasActionButton && hasDrawings && onOpenDrawing && (
            <Button
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                onOpenDrawing(firstDrawing);
              }}
              startIcon={<Icon name="view1" color="#fff" size={18} />}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
                bgcolor: COLORS.PRIMARY,
                color: "#fff",
                px: 2,
                py: 1.2,
                borderRadius: 2,
                cursor: "pointer",
                fontWeight: 600,
                whiteSpace: "nowrap",
                textTransform: "none",
                height: "40px",
                minWidth: "180px",
                "&:hover": {
                  bgcolor: COLORS.PRIMARY_DARK,
                },
              }}
            >
              View Drawing
            </Button>
          )}
        </Box>
      </Box>

      {/* Drawing Section - Using Flexbox */}
      {hasDrawings && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            width: "100%",
            mt: 1,
          }}
        >
          {/* Drawing Number */}
          <Box sx={{
            flex: "0 0 auto",
            display: "flex",
            alignItems: "center",
            whiteSpace: "nowrap",
            mr: 2,
            width: "120px"
          }}>
            <Typography
              sx={{
                ...TYPOGRAPHY.BODY_SMALL,
                color: COLORS.TEXT,
                mr: 0.5,
                whiteSpace: "nowrap",
              }}
            >
              Drawing Number:
            </Typography>
            <Typography
              sx={{
                ...TYPOGRAPHY.BODY_STANDARD,
                color: COLORS.PRIMARY,
                fontWeight: 600,
                whiteSpace: "nowrap",
                wordBreak: "keep-all",
              }}
            >
              {firstDrawing?.drawingNumber || "AKR-123"}
            </Typography>
          </Box>

          {/* Empty spaces for columns 2 and 3 */}
          <Box sx={{ flex: "0 0 auto", mr: 2, width: "180px" }} />
          <Box sx={{ flex: "0 0 auto", width: "200px", mr: 2 }} /> {/* Updated width and added margin */}

          {/* View Drawing Button for cases with action button - FIXED: Proper alignment */}
          {hasActionButton && onOpenDrawing && (
            <Box
              sx={{
                flex: "0 0 auto",
                width: "180px",
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center", // Keep center for drawing button
              }}
            >
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  onOpenDrawing(firstDrawing);
                }}
                startIcon={<Icon name="view1" color="#fff" size={18} />}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 1,
                  bgcolor: COLORS.PRIMARY,
                  color: "#fff",
                  px: 2,
                  py: 1.2,
                  borderRadius: 2,
                  cursor: "pointer",
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                  textTransform: "none",
                  height: "40px",
                  minWidth: "180px",
                  "&:hover": {
                    bgcolor: COLORS.PRIMARY_DARK,
                  },
                }}
              >
                View Drawing
              </Button>
            </Box>
          )}
        </Box>
      )}

      {/* Modals */}
      <EditDateModal
        open={editDateModalOpen}
        onClose={() => {
          if (isMounted.current) {
            setEditDateModalOpen(false);
          }
        }}
        dateEdits={dateEdits}
        setDateEdits={setDateEdits}
        onSave={handleDateSave}
        loading={false}
      />

      <EditAdditionalTimeModal
        open={editAdditionalTimeModalOpen}
        onClose={() => {
          if (isMounted.current) {
            setEditAdditionalTimeModalOpen(false);
          }
        }}
        additionalTime={additionalTimeEdit}
        setAdditionalTime={setAdditionalTimeEdit}
        onSave={handleAdditionalTimeSave}
        loading={false}
      />
    </>
  );
};

// Checklist Table Component
const ChecklistTable = ({
  checklistData,
  editable = true,
  onAnswerChange,
  onRemarksChange,
}) => {
  if (!checklistData?.sections || checklistData.sections.length === 0) {
    return (
      <Typography sx={{ color: COLORS.TEXT, textAlign: "center", py: 3 }}>
        No checklist data available
      </Typography>
    );
  }

  const gridCols = "minmax(0, 4fr) 60px 60px 80px minmax(0, 2fr)";

  return (
    <Box sx={{ mt: 1 }}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: gridCols,
          alignItems: "center",
          pb: 1,
          borderBottom: `2px solid ${COLORS.SECONDARY}`,
        }}
      >
        <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.TEXT }}>
          Checklist Item
        </Typography>
        <Typography
          align="center"
          sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.TEXT }}
        >
          Yes
        </Typography>
        <Typography
          align="center"
          sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.TEXT }}
        >
          No
        </Typography>
        <Typography
          align="center"
          sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.TEXT }}
        >
          NA
        </Typography>
        <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.TEXT }}>
          Remarks
        </Typography>
      </Box>

      {checklistData.sections.map((section, sectionIndex) => (
        <Box key={sectionIndex}>
          {section.name && (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: gridCols,
                alignItems: "center",
                py: 1.5,
                borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                backgroundColor: COLORS.LIGHT_SECONDARY + "20",
              }}
            >
              <Typography
                sx={{
                  color: COLORS.PRIMARY,
                  ...TYPOGRAPHY.BODY_LARGE,
                  fontWeight: 600,
                  pl: 2,
                }}
              >
                {section.name}
              </Typography>
              <Box></Box>
              <Box></Box>
              <Box></Box>
              <Box></Box>
            </Box>
          )}

          {section.items &&
            section.items.map((item, itemIndex) => (
              <Box
                key={itemIndex}
                sx={{
                  display: "grid",
                  gridTemplateColumns: gridCols,
                  alignItems: "flex-start",
                  py: 1.5,
                  borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                }}
              >
                <Box sx={{ pr: 2 }}>
                  <Typography
                    sx={{
                      color: COLORS.PRIMARY,
                      ...TYPOGRAPHY.BODY_STANDARD,
                      fontWeight: 600,
                      mb: 0.5,
                    }}
                  >
                    {item.srNo}. {item.question}
                  </Typography>
                  {item.testDescription && (
                    <Typography
                      sx={{
                        color: COLORS.SECONDARY,
                        ...TYPOGRAPHY.BODY_SMALL,
                        fontStyle: "italic",
                        mb: 0.5,
                      }}
                    >
                      Test: {item.testDescription}
                    </Typography>
                  )}
                  {item.acceptanceCriteria && (
                    <Typography
                      sx={{
                        color: COLORS.SECONDARY,
                        ...TYPOGRAPHY.BODY_SMALL,
                        mb: 0.5,
                      }}
                    >
                      Criteria: {item.acceptanceCriteria}
                    </Typography>
                  )}
                </Box>

                <Box sx={{ textAlign: "center" }}>
                  {editable ? (
                    <IconButton
                      size="small"
                      onClick={() =>
                        onAnswerChange(sectionIndex, itemIndex, "YES")
                      }
                      sx={{
                        color:
                          item.answer === "YES"
                            ? COLORS.PRIMARY
                            : COLORS.LIGHT_SECONDARY,
                        "&:hover": {
                          transform: "scale(1.2)",
                        },
                      }}
                    >
                      <Icon
                        name={item.answer === "YES" ? "check2" : "check2"}
                        size={20}
                      />
                    </IconButton>
                  ) : item.answer === "YES" ? (
                    <Icon name="check2" color={COLORS.PRIMARY} size={20} />
                  ) : null}
                </Box>

                <Box sx={{ textAlign: "center" }}>
                  {editable ? (
                    <IconButton
                      size="small"
                      onClick={() =>
                        onAnswerChange(sectionIndex, itemIndex, "NO")
                      }
                      sx={{
                        color:
                          item.answer === "NO"
                            ? COLORS.PRIMARY
                            : COLORS.LIGHT_SECONDARY,
                        "&:hover": {
                          transform: "scale(1.2)",
                        },
                      }}
                    >
                      <Icon
                        name={item.answer === "NO" ? "check2" : "check2"}
                        size={20}
                      />
                    </IconButton>
                  ) : item.answer === "NO" ? (
                    <Icon name="check2" color={COLORS.PRIMARY} size={20} />
                  ) : null}
                </Box>

                <Box sx={{ textAlign: "center" }}>
                  {editable ? (
                    <IconButton
                      size="small"
                      onClick={() =>
                        onAnswerChange(sectionIndex, itemIndex, "NA")
                      }
                      sx={{
                        color:
                          item.answer === "NA"
                            ? COLORS.PRIMARY
                            : COLORS.LIGHT_SECONDARY,
                        "&:hover": {
                          transform: "scale(1.2)",
                        },
                      }}
                    >
                      <Icon
                        name={item.answer === "NA" ? "check2" : "check2"}
                        size={20}
                      />
                    </IconButton>
                  ) : item.answer === "NA" ? (
                    <Icon name="check2" color={COLORS.PRIMARY} size={20} />
                  ) : null}
                </Box>

                {editable ? (
                  <TextField
                    value={item.remarks || ""}
                    onChange={(e) =>
                      onRemarksChange(sectionIndex, itemIndex, e.target.value)
                    }
                    placeholder="Add remarks..."
                    multiline
                    minRows={1}
                    maxRows={8}
                    size="small"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        alignItems: "flex-start",
                        "& fieldset": { borderColor: COLORS.LIGHT_SECONDARY },
                        "&:hover fieldset": { borderColor: COLORS.SECONDARY },
                      },
                      "& textarea": {
                        lineHeight: 1.5,
                      },
                    }}
                  />
                ) : (
                  <Typography
                    sx={{
                      color: COLORS.PRIMARY,
                      whiteSpace: "pre-line",
                      wordBreak: "break-word",
                      ...TYPOGRAPHY.BODY_STANDARD,
                    }}
                  >
                    {item.remarks || "-"}
                  </Typography>
                )}
              </Box>
            ))}
        </Box>
      ))}
    </Box>
  );
};

const PhotoGallery = ({
  photos,
  onDeletePhoto,
  onAddPhoto,
  editable = true,
}) => {
  const handleDelete = (index) => {
    onDeletePhoto(index);
  };

  return (
    <Box sx={{ mb: 3 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography sx={{ ...TYPOGRAPHY.H4, color: "#000" }}>Photos</Typography>

        {editable && photos.length < 6 && (
          <Button
            variant="outlined"
            startIcon={<Icon name="camera" />}
            onClick={onAddPhoto}
            sx={{ whiteSpace: "nowrap" }}
          >
            Add Photo
          </Button>
        )}
      </Box>

      {photos.length === 0 ? (
        <Typography sx={{ color: COLORS.TEXT, textAlign: "center", py: 3 }}>
          No photos uploaded
        </Typography>
      ) : (
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 2,
            justifyContent: "space-between",
          }}
        >
          {photos.map((photo, index) => (
            <Box
              key={index}
              sx={{
                flex: "0 0 calc(50% - 8px)",
                mb: 2,
                position: "relative",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Box
                sx={{
                  position: "relative",
                  width: "100%",
                  height: 240, // Fixed container height
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "#fff",
                  borderRadius: 1,
                  border: `2px solid ${COLORS.LIGHT_SECONDARY}`,
                  overflow: "hidden",
                }}
              >
                <Box
                  component="img"
                  src={getFullImageUrl(photo.image)}
                  alt={`Checklist photo ${index + 1}`}
                  sx={{
                    width: "100%",
                    height: "100%",
                    objectFit: "contain", // CHANGED from 'cover' to 'contain'
                    display: "block",
                  }}
                  onLoad={(e) => {
                    // Optional: You can add logic here to handle different aspect ratios
                    const img = e.target;
                    const aspectRatio = img.naturalWidth / img.naturalHeight;

                    // If portrait (height > width), center it differently
                    if (aspectRatio < 1) {
                      // Portrait image - keep as is
                    } else {
                      // Landscape image
                    }
                  }}
                />

                {editable && (
                  <IconButton
                    onClick={() => handleDelete(index)}
                    sx={{
                      position: "absolute",
                      top: 8,
                      right: 8,
                      backgroundColor: "rgba(0, 0, 0, 0.6)",
                      color: "#fff",
                      "&:hover": {
                        backgroundColor: "rgba(0, 0, 0, 0.8)",
                        transform: "scale(1.1)",
                      },
                      width: 30,
                      height: 30,
                    }}
                    size="small"
                  >
                    <Icon name="close" size={16} />
                  </IconButton>
                )}
              </Box>

              {photo.description && (
                <Typography
                  sx={{
                    ...TYPOGRAPHY.BODY_SMALL,
                    color: COLORS.TEXT,
                    mt: 0.5,
                    wordBreak: "break-word",
                    textAlign: "center",
                  }}
                >
                  {photo.description}
                </Typography>
              )}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};
// Other Observations Component
const OtherObservations = ({
  observations,
  onAddObservation,
  onDeleteObservation,
  editable = true,
}) => {
  const [newObservation, setNewObservation] = useState("");
  const [tempObservations, setTempObservations] = useState([]);

  useEffect(() => {
    if (observations && Array.isArray(observations)) {
      const formattedObs = observations.map((obs, index) => ({
        id: index,
        text:
          typeof obs === "string"
            ? obs
            : obs.observation || obs.text || JSON.stringify(obs),
      }));
      setTempObservations(formattedObs);
    } else {
      setTempObservations([]);
    }
  }, [observations]);

  const handleAdd = () => {
    if (newObservation.trim()) {
      const newItem = {
        id: tempObservations.length,
        text: newObservation.trim(),
      };
      const updatedObs = [...tempObservations, newItem];
      setTempObservations(updatedObs);

      const backendFormat = updatedObs.map((item) => ({
        observation: item.text,
      }));
      onAddObservation(backendFormat);

      setNewObservation("");
    }
  };

  const handleDelete = (id) => {
    const updatedObs = tempObservations.filter((item) => item.id !== id);
    setTempObservations(updatedObs);

    const backendFormat = updatedObs.map((item) => ({
      observation: item.text,
    }));
    onDeleteObservation(backendFormat);
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Typography sx={{ ...TYPOGRAPHY.H4, color: "#000", mb: 2 }}>
        Other Observations
      </Typography>

      {editable && (
        <Box sx={{ display: "flex", gap: 1, mb: 2, alignItems: "flex-start" }}>
          <TextField
            fullWidth
            multiline
            minRows={1}
            maxRows={6}
            placeholder="Add a new observation..."
            value={newObservation}
            onChange={(e) => setNewObservation(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleAdd();
              }
            }}
            size="small"
            sx={{
              "& .MuiOutlinedInput-root": {
                minHeight: "40px",
                alignItems: "flex-start",
              },
              "& .MuiInputBase-input": {
                padding: "4px 8px",
                lineHeight: "15px",
                boxSizing: "border-box",
              },
            }}
          />

          <Button
            variant="contained"
            onClick={handleAdd}
            disabled={!newObservation.trim()}
            sx={{
              bgcolor: COLORS.SECONDARY,
              height: "40px",
              whiteSpace: "nowrap",
            }}
          >
            <Icon name="plus2" />
            Add
          </Button>
        </Box>
      )}

      {tempObservations.length === 0 ? (
        <Typography
          sx={{
            color: COLORS.TEXT,
            textAlign: "center",
            py: 1,
            fontStyle: "italic",
          }}
        >
          No observations added yet
        </Typography>
      ) : (
        <Box sx={{ mt: 1, display: "flex", flexDirection: "column", gap: 1 }}>
          {tempObservations.map((obs, idx) => (
            <Paper
              key={obs.id || idx}
              variant="outlined"
              sx={{
                minHeight: "40px",
                px: 1.5,
                py: 0.5,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                backgroundColor: "#fff",
              }}
            >
              <Typography
                sx={{
                  ...TYPOGRAPHY.BODY_STANDARD,
                  color: COLORS.PRIMARY,
                  whiteSpace: "normal",
                  wordBreak: "break-word",
                  flex: 1,
                  pr: 1,
                }}
              >
                {obs.text}
              </Typography>

              {editable && (
                <IconButton size="small" onClick={() => handleDelete(obs.id)}>
                  <Icon name="close2" color={COLORS.ERROR} />
                </IconButton>
              )}
            </Paper>
          ))}
        </Box>
      )}
    </Box>
  );
};

const AdminRemarks = ({
  remarks,
  onAddRemarks,
  onDeleteRemark,
  editable = true,
  userRole,
}) => {
  const [newRemark, setNewRemark] = useState("");
  const [tempRemarks, setTempRemarks] = useState([]);

  useEffect(() => {
    if (remarks) {
      if (Array.isArray(remarks)) {
        setTempRemarks(
          remarks.map((item, index) => ({
            id: index,
            text:
              typeof item === "string"
                ? item
                : item.text || item.remark || item.observation || "",
          }))
        );
      } else if (typeof remarks === "string") {
        setTempRemarks([{ id: 0, text: remarks }]);
      }
    } else {
      setTempRemarks([]);
    }
  }, [remarks]);

  const handleAdd = (e) => {
    if (e) e.preventDefault();
    if (!newRemark.trim()) return;

    const updated = [
      ...tempRemarks,
      { id: tempRemarks.length, text: newRemark.trim() },
    ];
    setTempRemarks(updated);
    onAddRemarks(updated.map((r) => r.text));
    setNewRemark("");
  };

  const handleDelete = (id) => {
    // Find the index to delete
    const remarkIndex = tempRemarks.findIndex((r) => r.id === id);
    if (remarkIndex === -1) return;

    // Call the delete API
    if (onDeleteRemark) {
      onDeleteRemark(remarkIndex);
    }

    // Also update local state optimistically
    const updated = tempRemarks.filter((r) => r.id !== id);
    setTempRemarks(updated);
  };

  if (!["ADMIN", "SUPERADMIN"].includes(userRole)) return null;

  return (
    <Box sx={{ mb: 2 }}>
      <Typography sx={{ ...TYPOGRAPHY.H4, color: "#000", mb: 1 }}>
        Admin Remarks
      </Typography>

      {editable && (
        <Box
          component="form"
          onSubmit={handleAdd}
          sx={{ display: "flex", flexDirection: "column", gap: 1 }}
        >
          <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Add Admin Remark"
              value={newRemark}
              onChange={(e) => setNewRemark(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleAdd(e);
                }
              }}
              multiline
              minRows={1}
              maxRows={4}
              sx={{
                "& .MuiOutlinedInput-root": {
                  height: "40px",
                  alignItems: "center",
                },
                "& textarea": {
                  lineHeight: "1.4",
                },
              }}
            />

            <Button
              type="submit"
              variant="contained"
              disabled={!newRemark.trim()}
              sx={{
                bgcolor: COLORS.SECONDARY,
                height: "40px",
                whiteSpace: "nowrap",
                px: 2,
              }}
            >
              Add
            </Button>
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {tempRemarks.map((item) => (
              <Paper
                key={item.id}
                variant="outlined"
                sx={{
                  p: 1.2,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  backgroundColor: "#fff",
                }}
              >
                <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.PRIMARY }}>
                  {item.text}
                </Typography>

                {editable && (
                  <IconButton
                    onClick={() => handleDelete(item.id)}
                    size="small"
                    type="button"
                  >
                    <Icon name="close2" color={COLORS.ERROR} />
                  </IconButton>
                )}
              </Paper>
            ))}
          </Box>
        </Box>
      )}

      {!editable && tempRemarks.length === 0 && (
        <Typography sx={{ fontStyle: "italic", color: COLORS.TEXT }}>
          No admin remarks added yet
        </Typography>
      )}
    </Box>
  );
};
// UPDATED: Signatures Display Component - No signature shown initially
const SignaturesDisplay = ({
  signatures,
  onAddSignature,
  onApplyStoredSignature, // NEW: For applying stored signature directly
  editable = true,
  userRole,
  pocName,
  assignedEngineerName,
  adminDetails,
  userData,
  userSignature,
  adminSignature,
}) => {
  const formatDateTime = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    const dateStr = date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
    }).replace(/\//g, '/');
    const timeStr = date.toLocaleTimeString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
    });
    return `${dateStr} | ${timeStr}`;
  };

  const { engineer, clientSiteEngineer, admin } = signatures || {};

  // Check if signature should be displayed (only if signed for this checklist)
  const shouldDisplaySignature = (role) => {
    switch (role) {
      case "engineer":
        return !!engineer?.signature;
      case "clientSiteEngineer":
        return !!clientSiteEngineer?.signature;
      case "admin":
        return !!admin?.signature;
      default:
        return false;
    }
  };

  // Determine if current user can sign
  const canSign = (role) => {
    const currentUserRole = userRole;

    if (role === "engineer") {
      return editable && currentUserRole === "ENGINEER";
    } else if (role === "clientSiteEngineer") {
      return editable && currentUserRole === "ENGINEER";
    } else if (role === "admin") {
      return (
        editable &&
        (currentUserRole === "ADMIN" || currentUserRole === "SUPERADMIN")
      );
    }
    return false;
  };

  // Check if user has stored signature
  const hasStoredSignature = (role) => {
    if (role === "engineer") {
      return userSignature?.hasSignature && userSignature?.signature;
    } else if (role === "admin") {
      return adminSignature?.hasSignature && adminSignature?.signature;
    }
    return false;
  };

  // Get the name to display for each role
  const getDisplayName = (role, signatureData) => {
    // If signature exists, show the name from signature
    if (signatureData?.name) {
      return signatureData.name;
    }

    // If no signature yet, show default names
    switch (role) {
      case 'engineer':
        return assignedEngineerName || "Engineer";
      case 'clientSiteEngineer':
        return pocName || "Site Engineer";
      case 'admin':
        // Show actual admin name from adminDetails
        return adminDetails?.name || "PMC ";
      default:
        return "Not signed";
    }
  };

  // Handle sign button click
  const handleSignClick = (role) => {
    console.log(
      "Sign click for role:",
      role,
      "Has stored signature:",
      hasStoredSignature(role)
    );

    // For site engineer, always open modal (no stored signature)
    if (role === "clientSiteEngineer") {
      onAddSignature(role);
      return;
    }

    // For engineer and admin, check if stored signature exists
    if (hasStoredSignature(role)) {
      console.log("Applying stored signature for:", role);
      // Apply stored signature directly
      onApplyStoredSignature(role);
    } else {
      console.log("No stored signature, opening modal for:", role);
      // No stored signature, open modal
      onAddSignature(role);
    }
  };

  // Get button text based on role and signature status
  const getButtonText = (role) => {
    if (role === 'clientSiteEngineer') {
      return 'Sign for Site Engineer';
    }

    if (hasStoredSignature(role)) {
      return `Sign as ${role === 'engineer' ? 'PMC Engineer' : 'PMC '}`;
    }

    return `Sign as ${role === 'engineer' ? 'PMC Engineer' : 'PMC '}`;
  };

  return (
    <Box sx={{ mt: 4 }}>
      <Typography sx={{ ...TYPOGRAPHY.H4, color: "#000", mb: 3 }}>
        Signatures
      </Typography>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: 4,
          textAlign: "center",
        }}
      >
        {/* Engineer Signature */}
        <Box>
          {shouldDisplaySignature('engineer') ? (
            <Box sx={{ height: 80, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Box
                component="img"
                src={getFullImageUrl(engineer.signature)}
                alt="Engineer Signature"
                sx={{
                  maxHeight: '100%',
                  maxWidth: '100%',
                  objectFit: 'contain',
                }}
              />
            </Box>
          ) : (
            <Box sx={{
              height: 80,
              borderBottom: `2px solid ${COLORS.LIGHT_SECONDARY}`,
              display: 'flex',
              alignItems: 'flex-end',
              justifyContent: 'center'
            }} />
          )}
          <Typography sx={{ ...TYPOGRAPHY.BODY_SMALL, color: COLORS.TEXT, mt: 1 }}>
            PMC Engineer
          </Typography>
          <Typography sx={{ ...TYPOGRAPHY.BODY_STANDARD, color: COLORS.PRIMARY, mt: 0.5, fontWeight: 500 }}>
            {getDisplayName('engineer', engineer)}
          </Typography>
          {engineer?.signedAt && (
            <Typography sx={{ ...TYPOGRAPHY.CAPTION, color: COLORS.SECONDARY, mt: 0.5 }}>
              {formatDateTime(engineer.signedAt)}
            </Typography>
          )}
          {canSign('engineer') && !shouldDisplaySignature('engineer') && (
            <Button
              size="small"
              variant="outlined"
              onClick={() => handleSignClick('engineer')}
              sx={{ mt: 1 }}
            >
              {getButtonText('engineer')}
            </Button>
          )}
        </Box>

        {/* Site Engineer Signature */}
        <Box>
          {shouldDisplaySignature("clientSiteEngineer") ? (
            <Box
              sx={{
                height: 80,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Box
                component="img"
                src={getFullImageUrl(clientSiteEngineer.signature)}
                alt="Site Engineer Signature"
                sx={{
                  maxHeight: "100%",
                  maxWidth: "100%",
                  objectFit: "contain",
                }}
              />
            </Box>
          ) : (
            <Box
              sx={{
                height: 80,
                borderBottom: `2px solid ${COLORS.LIGHT_SECONDARY}`,
                display: "flex",
                alignItems: "flex-end",
                justifyContent: "center",
              }}
            />
          )}
          <Typography
            sx={{ ...TYPOGRAPHY.BODY_SMALL, color: COLORS.TEXT, mt: 1 }}
          >
            Site Engineer
          </Typography>
          <Typography
            sx={{
              ...TYPOGRAPHY.BODY_STANDARD,
              color: COLORS.PRIMARY,
              mt: 0.5,
              fontWeight: 500,
            }}
          >
            {getDisplayName("clientSiteEngineer", clientSiteEngineer)}
          </Typography>
          {clientSiteEngineer?.signedAt && (
            <Typography
              sx={{ ...TYPOGRAPHY.CAPTION, color: COLORS.SECONDARY, mt: 0.5 }}
            >
              {formatDateTime(clientSiteEngineer.signedAt)}
            </Typography>
          )}
          {canSign("clientSiteEngineer") &&
            !shouldDisplaySignature("clientSiteEngineer") && (
              <Button
                size="small"
                variant="outlined"
                onClick={() => handleSignClick("clientSiteEngineer")}
                sx={{ mt: 1 }}
              >
                {getButtonText("clientSiteEngineer")}
              </Button>
            )}
        </Box>

        {/* Admin Signature */}
        <Box>
          {shouldDisplaySignature('admin') ? (
            <Box sx={{ height: 80, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Box
                component="img"
                src={getFullImageUrl(admin.signature)}
                alt="Admin Signature"
                sx={{
                  maxHeight: '100%',
                  maxWidth: '100%',
                  objectFit: 'contain',
                }}
              />
            </Box>
          ) : (
            <Box sx={{
              height: 80,
              borderBottom: `2px solid ${COLORS.LIGHT_SECONDARY}`,
              display: 'flex',
              alignItems: 'flex-end',
              justifyContent: 'center'
            }} />
          )}
          <Typography sx={{ ...TYPOGRAPHY.BODY_SMALL, color: COLORS.TEXT, mt: 1 }}>
            PMC
          </Typography>
          <Typography sx={{ ...TYPOGRAPHY.BODY_STANDARD, color: COLORS.PRIMARY, mt: 0.5, fontWeight: 500 }}>
            {getDisplayName('admin', admin)}
          </Typography>
          {admin?.signedAt && (
            <Typography sx={{ ...TYPOGRAPHY.CAPTION, color: COLORS.SECONDARY, mt: 0.5 }}>
              {formatDateTime(admin.signedAt)}
            </Typography>
          )}
          {canSign('admin') && !shouldDisplaySignature('admin') && (
            <Button
              size="small"
              variant="outlined"
              onClick={() => handleSignClick('admin')}
              sx={{ mt: 1 }}
            >
              {getButtonText('admin')}
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
};

// Info Column Component
const InfoColumn = ({ title, data, showIcon = true }) => {
  const getIconName = () => {
    if (title.includes("Admin")) return "profile";
    if (title.includes("Engineer")) return "profile";
    if (title.includes("Client")) return "profile";
    if (title.includes("POC")) return "profile";
    return "profile";
  };

  return (
    <Box
      sx={{
        position: "relative",
        flex: 1,
        pl: 3,
        minWidth: 0,
      }}
    >
      <Box
        sx={{
          position: "absolute",
          left: 15,
          top: 20,
          bottom: -8,
          width: "2px",
          bgcolor: COLORS.LIGHT_SECONDARY,
        }}
      />

      <Typography
        sx={{
          color: COLORS.TEXT,
          ...TYPOGRAPHY.BODY_LARGE,
          mb: 1,
          ml: -1.8,
          wordBreak: "break-word",
        }}
      >
        {title}
      </Typography>

      <Box>
        {data?.name && (
          <Box sx={{ display: "flex", alignItems: "center", mb: 0.8 }}>
            {showIcon && (
              <Icon
                name={getIconName()}
                color={COLORS.LIGHT_SECONDARY}
                size={18}
              />
            )}
            <Typography
              sx={{
                ...TYPOGRAPHY.BODY_STANDARD,
                ml: showIcon ? 1 : 0,
                color: COLORS.SECONDARY,
                wordBreak: "break-word",
              }}
            >
              {data.name}
            </Typography>
          </Box>
        )}

        {data?.email && data.email !== "-" && (
          <Box sx={{ display: "flex", alignItems: "center", mb: 0.8 }}>
            {showIcon && (
              <Icon name="at" color={COLORS.LIGHT_SECONDARY} size={16} />
            )}
            <Typography
              sx={{
                ...TYPOGRAPHY.BODY_STANDARD,
                ml: showIcon ? 1 : 0,
                color: COLORS.SECONDARY,
                wordBreak: "break-word",
              }}
            >
              {data.email}
            </Typography>
          </Box>
        )}

        {data?.phone && data.phone !== "-" && (
          <Box sx={{ display: "flex", alignItems: "center" }}>
            {showIcon && (
              <Icon name="phoneDone" color={COLORS.LIGHT_SECONDARY} size={16} />
            )}
            <Typography
              sx={{
                ...TYPOGRAPHY.BODY_STANDARD,
                ml: showIcon ? 1 : 0,
                color: COLORS.SECONDARY,
                wordBreak: "break-word",
              }}
            >
              {data.phone}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

// UPDATED: Signature Modal Component - Don't pre-load signatures
const SignatureModal = ({
  open,
  onClose,
  onSave,
  role,
  userData,
  pocName,
  assignedEngineerName,
}) => {
  const canvasRef = useRef(null);
  const [signature, setSignature] = useState(null);
  const [name, setName] = useState("");

  useEffect(() => {
    if (open) {
      // Set name based on role
      if (role === 'engineer') {
        // Use assigned engineer name for PMC Engineer
        setName(assignedEngineerName || userData?.name || "");
      } else if (role === 'clientSiteEngineer') {
        // Use POC name for site engineer
        setName(pocName || "");
      } else if (role === 'admin') {
        // Admin name
        setName("Admin");
      }

      // Clear canvas every time modal opens
      if (canvasRef.current) {
        canvasRef.current.clear();
        setSignature(null);
      }
    }
  }, [open, role, userData, pocName, assignedEngineerName]);

  const handleClear = () => {
    if (canvasRef.current) {
      canvasRef.current.clear();
      setSignature(null);
    }
  };

  const handleSave = () => {
    if (canvasRef.current) {
      const dataUrl = canvasRef.current.toDataURL("image/png");

      const saveData = {
        signature: dataUrl,
        // Include name based on role
        ...(role === "clientSiteEngineer" && {
          name: name.trim(),
        }),
        // For engineer role, name comes from userData
        // For admin, name comes from backend auth
      };

      onSave(saveData, role);
    }
  };

  if (!open) return null;

  const getModalTitle = () => {
    switch (role) {
      case 'engineer':
        return `Sign as PMC Engineer (${assignedEngineerName || userData?.name || 'Engineer'})`;
      case 'clientSiteEngineer':
        return `Sign for Site Engineer (${pocName || 'POC'})`;
      case 'admin':
        return 'Sign as PMC ';
      default:
        return 'Add Signature';
    }
  };

  const getInstructions = () => {
    switch (role) {
      case 'engineer':
        return `You are signing as ${assignedEngineerName || userData?.name || 'Engineer'} for PMC Engineer.`;
      case 'clientSiteEngineer':
        return `You are signing for Site Engineer: ${pocName || 'POC'}.`;
      case 'admin':
        return 'You are signing as PMC .';
      default:
        return 'Draw your signature in the box below';
    }
  };

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        bgcolor: "rgba(0,0,0,0.5)",
        zIndex: 1400,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: 500,
          maxHeight: "95vh",
          bgcolor: "#fff",
          borderRadius: 2,
          boxShadow: "0px 6px 18px rgba(0,0,0,0.15)",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            p: 2,
            borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
            flexShrink: 0,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.PRIMARY }}>
            {getModalTitle()}
          </Typography>
          <IconButton onClick={onClose} size="small" sx={{ ml: 2 }}>
            <Icon name="close" />
          </IconButton>
        </Box>

        <Box
          sx={{
            p: 2,
            flex: 1,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Typography
            sx={{
              ...TYPOGRAPHY.BODY_SMALL,
              color: COLORS.TEXT,
              mb: 2,
              p: 1.5,
              bgcolor: COLORS.LIGHT_SECONDARY + "15",
              borderRadius: 1,
              borderLeft: `3px solid ${COLORS.SECONDARY}`,
            }}
          >
            {getInstructions()}
          </Typography>

          {role === "clientSiteEngineer" && (
            <TextField
              fullWidth
              label="Site Engineer Name *"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter Site Engineer's full name"
              size="small"
              sx={{ mb: 3 }}
              required
            />
          )}

          <Typography
            sx={{
              ...TYPOGRAPHY.BODY_SMALL,
              color: COLORS.TEXT,
              mb: 1.5,
            }}
          >
            Draw your signature in the box below
          </Typography>

          <Box
            sx={{
              flex: 1,
              border: `1px solid ${COLORS.LIGHT_SECONDARY}`,
              borderRadius: 1,
              overflow: "hidden",
              backgroundColor: "#f8f8f8",
              position: "relative",
              height: 200,
            }}
          >
            <SignatureCanvas
              ref={canvasRef}
              onSignatureChange={setSignature}
              canvasProps={{
                style: {
                  width: "100%",
                  height: "100%",
                  backgroundColor: "#fff",
                },
              }}
            />
          </Box>
        </Box>

        <Box
          sx={{
            p: 2,
            display: "flex",
            justifyContent: "flex-end",
            gap: 1.5,
            borderTop: `1px solid ${COLORS.LIGHT_SECONDARY}`,
            flexShrink: 0,
          }}
        >
          <Button onClick={onClose} size="small">
            Cancel
          </Button>
          <Button onClick={handleClear} color="error" size="small">
            Clear
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={
              !signature || (role === "clientSiteEngineer" && !name.trim())
            }
            sx={{
              bgcolor: COLORS.SECONDARY,
              "&.Mui-disabled": {
                bgcolor: COLORS.LIGHT_SECONDARY,
              },
            }}
            size="small"
          >
            Save Signature
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

// Toast Provider Component
const ToastProvider = ({ children }) => {
  const [toast, setToast] = useState({
    open: false,
    type: "info",
    title: "",
    message: "",
  });

  const showToast = (type, title, message) => {
    setToast({
      open: true,
      type,
      title,
      message,
    });
  };

  const hideToast = () => {
    setToast((prev) => ({ ...prev, open: false }));
  };

  return (
    <ToastContext.Provider value={{ showToast, hideToast }}>
      {children}
      <AppToast
        open={toast.open}
        type={toast.type}
        title={toast.title}
        message={toast.message}
        onClose={hideToast}
        autoHideDuration={5000}
      />
    </ToastContext.Provider>
  );
};

// Main Component
const EditChecklistSidebar = ({ open, onClose, projectId, mode = "edit", onChangesDetected }) => {
  const { token, hasRole, userData } = useAuth();
  const { showToast } = useToast();
  const [newReplies, setNewReplies] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [project, setProject] = useState(null);
  const [checklist, setChecklist] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [clientDetails, setClientDetails] = useState(null);
  const [adminDetails, setAdminDetails] = useState(null);
  const [generalContent, setGeneralContent] = useState({
    thankYou: null,
    termsConditions: null,
  });
  const [signatures, setSignatures] = useState({
    engineer: null,
    clientSiteEngineer: null,
    admin: null,
  });
  const [specificInstructions, setSpecificInstructions] = useState([]);
  const [newInstructionAnswer, setNewInstructionAnswer] = useState({});

  // Store original project status to prevent unwanted changes
  const [originalProjectStatus, setOriginalProjectStatus] = useState(null);

  // Track if any changes were made to prevent unnecessary backend calls
  const [hasChanges, setHasChanges] = useState(false);

  // NEW: Add states for user signatures
  const [userSignature, setUserSignature] = useState(null);
  const [adminSignature, setAdminSignature] = useState(null);
  const [viewSignatureModalOpen, setViewSignatureModalOpen] = useState(false);
  const [viewingSignature, setViewingSignature] = useState({
    url: null,
    role: null,
    name: null,
  });

  // Add state for modals
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [photoModalOpen, setPhotoModalOpen] = useState(false);
  const [signatureModalOpen, setSignatureModalOpen] = useState(false);
  const [currentSignatureRole, setCurrentSignatureRole] = useState(null);
  const [drawingModalOpen, setDrawingModalOpen] = useState(false);
  const [selectedDrawing, setSelectedDrawing] = useState(null);

  // NEW: Add state for date validation alert
  const [showDateAlert, setShowDateAlert] = useState(false);

  const getUserRole = () => {
    if (hasRole("CLIENT")) return "CLIENT";
    if (hasRole("ADMIN")) return "ADMIN";
    if (hasRole("SUPERADMIN")) return "SUPERADMIN";
    if (hasRole("ENGINEER")) return "ENGINEER";
    return null;
  };

  // Get user role once
  const userRole = getUserRole();

  // NEW: Calculate edit permission
  const canEditChecklist = getEditPermission(
    userRole,
    project?.status,
    project?.assignedVisitDate,
    mode === "edit"
  );

  // NEW: Date formatting helper
  const formatAssignedDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const isComponentMounted = useRef(true);

  useEffect(() => {
    isComponentMounted.current = true;


    return () => {
      isComponentMounted.current = false;
    };
  }, []);

  // NEW: Check date permissions when component opens
  useEffect(() => {
    if (open && project?.assignedVisitDate && ["ENGINEER", "ADMIN", "SUPERADMIN"].includes(userRole)) {
      const canEdit = getEditPermission(userRole, project.status, project.assignedVisitDate, mode === "edit");

      if (!canEdit) {
        const assignedDate = new Date(project.assignedVisitDate);
        const formattedDate = assignedDate.toLocaleDateString("en-IN", {
          day: "numeric",
          month: "short",
          year: "numeric",
        });

        const today = new Date();
        const todayFormatted = today.toLocaleDateString("en-IN", {
          day: "numeric",
          month: "short",
          year: "numeric",
        });

        if (isAssignedVisitDate(project.assignedVisitDate)) {
          setShowDateAlert(false);
        } else {
          setShowDateAlert(true);
        }
      } else {
        setShowDateAlert(false);
      }
    }
  }, [open, project, userRole, mode]);

  useEffect(() => {
    const fetchDataIfNeeded = async () => {
      if (!isComponentMounted.current) return;

      if (open && projectId) {
        // Reset original status and changes when opening a new project
        setOriginalProjectStatus(null);
        setHasChanges(false);
        await fetchData();
        await fetchGeneralContent();
        await fetchUserSignature();
      }
    };

    fetchDataIfNeeded();
  }, [open, projectId]);

  useEffect(() => {
    if (project?.specificInstructions?.instructions) {
      const formattedInstructions =
        project.specificInstructions.instructions.map((inst, idx) => {
          let instructionText,
            replies = [];

          if (typeof inst === "string") {
            instructionText = inst;
          } else if (typeof inst === "object") {
            instructionText = inst.text || inst;
            replies = inst.replies || [];
          } else {
            instructionText = String(inst);
          }

          return {
            index: idx,
            text: instructionText,
            replies: replies,
          };
        });

      setSpecificInstructions(formattedInstructions);

      const initialReplies = {};
      formattedInstructions.forEach((inst, idx) => {
        initialReplies[idx] = "";
      });
      setNewReplies(initialReplies);
    } else {
      setSpecificInstructions([]);
      setNewReplies({});
    }
  }, [project]);

  // Notify parent when changes are detected
  useEffect(() => {
    if (onChangesDetected && hasChanges) {
      console.log("Changes detected in EditCheckList, notifying parent");
      onChangesDetected();
    }
  }, [hasChanges, onChangesDetected]);

  const handleApplyStoredSignature = async (role) => {
    try {
      console.log("Applying stored signature for role:", role);

      let storedSignature = null;
      let name = "";

      // Get stored signature based on role
      if (role === "engineer" && userSignature?.hasSignature) {
        storedSignature = userSignature.signature;
        name = userSignature.userName || userData?.name || "Engineer";
      } else if (role === "admin" && adminSignature?.hasSignature) {
        storedSignature = adminSignature.signature;
        name = adminSignature.userName || adminDetails?.name || "Admin";
      }

      if (!storedSignature) {
        console.error("No stored signature found for role:", role);
        showToast("error", "Error", "No stored signature found");
        return;
      }

      console.log("Signature data URL:", storedSignature.substring(0, 100) + "...");

      // FIXED: Properly convert data URL to blob
      // Check if it's already a full URL or a data URL
      let blob;

      if (storedSignature.startsWith('data:')) {
        // It's a data URL - convert properly
        console.log("Converting data URL to blob...");

        // Extract the base64 data from the data URL
        const base64Data = storedSignature.split(',')[1];
        if (!base64Data) {
          throw new Error("Invalid data URL format");
        }

        // Convert base64 to binary
        const byteCharacters = atob(base64Data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);

        // Create blob with correct MIME type
        const mimeType = storedSignature.match(/^data:(.*?);/)[1];
        blob = new Blob([byteArray], { type: mimeType || 'image/png' });
      } else {
        // It's a regular URL - fetch it
        console.log("Fetching signature from URL...");
        const fetchResponse = await fetch(getFullImageUrl(storedSignature));
        blob = await fetchResponse.blob();
      }

      console.log("Blob created:", {
        size: blob.size,
        type: blob.type,
        name: 'signature.png'
      });

      // Prepare form data
      const formData = new FormData();
      formData.append("signature", blob, "signature.png");

      let apiEndpoint = "";

      if (hasRole("ENGINEER") && role === "engineer") {
        apiEndpoint = `${url}/api/engineer/sign-checklist/${checklist._id}`;
        formData.append("role", role);
        formData.append("name", name);
      } else if (
        (hasRole("ADMIN") || hasRole("SUPERADMIN")) &&
        role === "admin"
      ) {
        apiEndpoint = `${url}/api/admin/checklist/sign/${checklist._id}`;
        formData.append("name", name);
      }

      if (!apiEndpoint) {
        console.error("No API endpoint for role:", role);
        return;
      }

      console.log("Sending signature to:", apiEndpoint);

      // Log form data for debugging
      console.log("FormData entries:");
      for (let pair of formData.entries()) {
        console.log(pair[0], pair[1]);
      }

      // Send signature to backend
      const axiosResponse = await axios.post(
        apiEndpoint,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
          timeout: 30000,
        }
      );

      console.log("Signature applied successfully:", axiosResponse.data);

      // Update local state WITHOUT refreshing the whole page
      const responseSignatures =
        axiosResponse.data.signatures || axiosResponse.data.data?.signatures;
      if (responseSignatures) {
        const processedSignatures = {
          engineer: responseSignatures.engineer
            ? {
              ...responseSignatures.engineer,
              signature: getFullImageUrl(
                responseSignatures.engineer.signature
              ),
            }
            : null,
          clientSiteEngineer: responseSignatures.clientSiteEngineer
            ? {
              ...responseSignatures.clientSiteEngineer,
              signature: getFullImageUrl(
                responseSignatures.clientSiteEngineer.signature
              ),
            }
            : null,
          admin: responseSignatures.admin
            ? {
              ...responseSignatures.admin,
              signature: getFullImageUrl(responseSignatures.admin.signature),
            }
            : null,
        };
        setSignatures(processedSignatures);

        // Also update checklist status if needed
        if (checklist && axiosResponse.data.data) {
          setChecklist((prev) => ({
            ...prev,
            status: axiosResponse.data.data.status || prev.status,
            signatures: processedSignatures,
          }));
        }
      }

      showToast("success", "Success", "Signature applied successfully");

      // Mark that changes were made
      setHasChanges(true);
    } catch (err) {
      console.error("Error applying stored signature:", err);
      showToast(
        "error",
        "Error",
        err.response?.data?.message || "Failed to apply signature"
      );

      // NEW: Fallback to manual signature if stored signature fails
      console.log("Fallback to manual signature for role:", role);
      handleAddSignature(role);
    }
  };

  // NEW: Render date alert component
  const renderDateAlert = () => {
    if (!showDateAlert || !project?.assignedVisitDate) return null;

    const assignedDate = formatAssignedDate(project.assignedVisitDate);

    return (
      <Alert
        severity="warning"
        sx={{
          mb: 3,
          backgroundColor: '#FFF3CD',
          color: '#856404',
          border: '1px solid #FFEEBA'
        }}
        icon={<Icon name="calendarAlert" color="#856404" size={20} />}
      >
        <Typography sx={{ fontWeight: 600, mb: 0.5 }}>
          Editing Restricted
        </Typography>
        <Typography sx={{ fontSize: '14px' }}>
          This checklist can only be edited on the assigned visit date: <strong>{assignedDate}</strong>
        </Typography>
      </Alert>
    );
  };

  const handleDateUpdate = useCallback(async (dates) => {
    try {
      if (!isComponentMounted.current) return;

      setSaving(true);

      if (!dates.fromDate || !dates.toDate) {
        showToast("error", "Error", "Both from and to dates are required");
        return;
      }

      if (dates.toDate <= dates.fromDate) {
        showToast("error", "Error", "To date must be after from date");
        return;
      }

      // if (dates.fromDate < new Date()) {
      //   showToast("error", "Error", "Visit dates must be in the future");
      //   return;
      // }

      const payload = {
        fromDate: dates.fromDate.toISOString(),
        toDate: dates.toDate.toISOString(),
      };

      const response = await axios.put(
        `${url}/api/admin/edit-preferred-visit-datetime/${projectId}`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      showToast("success", "Success", "Visit date updated successfully");

      // Mark that changes were made
      setHasChanges(true);

      // Update project state immediately WITHOUT fetching
      if (isComponentMounted.current) {
        setProject((prev) => ({
          ...prev,
          preferredSiteVisitDate: {
            fromDate: dates.fromDate,
            toDate: dates.toDate,
          },
        }));
      }
    } catch (err) {
      console.error("Error updating visit date:", err);
      showToast(
        "error",
        "Error",
        err.response?.data?.message || "Failed to update visit date"
      );
    } finally {
      if (isComponentMounted.current) {
        setSaving(false);
      }
    }
  }, [projectId, token, showToast]);

  const handleDeleteAdminRemark = async (remarkIndex) => {
    try {
      if (!projectId) {
        showToast("error", "Error", "No project ID found");
        return;
      }

      const response = await axios.delete(
        `${url}/api/admin/checklist/remarks/${projectId}/${remarkIndex}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Update local state with remaining remarks
      if (response.data?.data?.remainingRemarks) {
        setProject((prev) => ({
          ...prev,
          adminRemarks: response.data.data.remainingRemarks,
        }));
      }

      showToast("success", "Success", "Admin remark deleted successfully");
    } catch (err) {
      console.error("Error deleting admin remark:", err);
      showToast(
        "error",
        "Error",
        err.response?.data?.message || "Failed to delete admin remark"
      );
    }
  };
  const handleDeletePhoto = async (photoIndex) => {
    try {
      if (!checklist?._id) {
        showToast("error", "Error", "No checklist found");
        return;
      }

      const response = await axios.delete(
        `${url}/api/engineer/photos/${checklist._id}/${photoIndex}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Update local state
      setPhotos(response.data.remainingPhotos || []);
      showToast("success", "Success", "Photo deleted successfully");
    } catch (err) {
      console.error("Error deleting photo:", err);
      showToast(
        "error",
        "Error",
        err.response?.data?.message || "Failed to delete photo"
      );
    }
  };

  const handleAdditionalTimeUpdate = async (additionalTime) => {
    try {
      setSaving(true);

      const payload = {
        fromDate: project.preferredSiteVisitDate.fromDate,
        toDate: project.preferredSiteVisitDate.toDate,
        additionalTime: additionalTime,
      };

      const response = await axios.put(
        `${url}/api/admin/edit-preferred-visit-datetime/${projectId}`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      showToast(
        "success",
        "Success",
        "Additional instructions updated successfully"
      );

      // Mark that changes were made
      setHasChanges(true);

      // Update project state immediately
      setProject((prev) => ({
        ...prev,
        additionalTime: additionalTime,
      }));
    } catch (err) {
      console.error("Error updating additional time:", err);
      showToast(
        "error",
        "Error",
        err.response?.data?.message ||
        "Failed to update additional instructions"
      );
    } finally {
      setSaving(false);
    }
  };
  const fetchGeneralContent = async () => {
    try {
      const [thankYouResponse, termsResponse] = await Promise.all([
        axios
          .get(`${url}/api/general/content/THANK_YOU`, {
            headers: { Authorization: `Bearer ${token}` },
          })
          .catch(() => ({ data: { data: null } })),

        axios
          .get(`${url}/api/general/content/TERMS_CONDITIONS`, {
            headers: { Authorization: `Bearer ${token}` },
          })
          .catch(() => ({ data: { data: [] } })),
      ]);

      const thankYouData = thankYouResponse.data?.data;
      const termsData = termsResponse.data?.data;

      setGeneralContent({
        thankYou: Array.isArray(thankYouData) ? thankYouData[0] : thankYouData,
        termsConditions: Array.isArray(termsData) ? termsData[0] : termsData,
      });
    } catch (err) {
      console.error("Error fetching general content:", err);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);

      const projectResponse = await axios.get(`${url}/api/client/requests`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { requestID: projectId },
      });

      if (!projectResponse.data?.requests?.length) {
        throw new Error("Project not found");
      }

      const projectData = projectResponse.data.requests[0];
      setProject(projectData);

      // Store the original status to prevent unwanted changes
      if (originalProjectStatus === null) {
        setOriginalProjectStatus(projectData.status);
      }

      if (projectData.clientID) {
        await fetchUserDetails(projectData.clientID, "client");
      } else {
        setClientDetails({
          name: projectData.nameOfClient || "N/A",
          email: "-",
          phone: projectData.phoneOfClient || "-",
        });
      }

      await fetchAdminDetails();
      await fetchInstructionReplies(projectId);

      try {
        const checklistResponse = await axios.get(
          `${url}/api/engineer/open-checklist/${projectId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (checklistResponse.data?.data) {
          const checklistData = checklistResponse.data.data;

          setChecklist(checklistData);
          setPhotos(checklistData.photos || []);

          const processedSignatures = {
            engineer: checklistData.signatures?.engineer
              ? {
                ...checklistData.signatures.engineer,
                signature: getFullImageUrl(
                  checklistData.signatures.engineer.signature
                ),
              }
              : null,
            clientSiteEngineer: checklistData.signatures?.clientSiteEngineer
              ? {
                ...checklistData.signatures.clientSiteEngineer,
                signature: getFullImageUrl(
                  checklistData.signatures.clientSiteEngineer.signature
                ),
              }
              : null,
            admin: checklistData.signatures?.admin
              ? {
                ...checklistData.signatures.admin,
                signature: getFullImageUrl(
                  checklistData.signatures.admin.signature
                ),
              }
              : null,
          };
          setSignatures(processedSignatures);
        }
      } catch (err) {
        console.log(
          "No checklist data found from engineer API, trying template API..."
        );

        if (projectData.status === "UNASSIGNED" && projectData.templateID) {
          try {
            const templateResponse = await axios.get(
              `${url}/api/admin/checklist-templates/${projectData.templateID}`,
              { headers: { Authorization: `Bearer ${token}` } }
            );

            if (templateResponse.data?.data) {
              const templateData = templateResponse.data.data;

              const checklistFromTemplate = {
                _id: `template_${projectData.templateID}`,
                title: templateData.title,
                description: templateData.description,
                sections: templateData.sections.map((sec) => ({
                  name: sec.name,
                  items: sec.items.map((item) => ({
                    srNo: item.srNo,
                    question: item.question,
                    answer: null,
                    remarks: "",
                    testDescription: item.testDescription,
                    acceptanceCriteria: item.acceptanceCriteria,
                  })),
                })),
                photos: [],
                otherObservations: [],
                signatures: {
                  engineer: null,
                  clientSiteEngineer: null,
                  admin: null,
                },
                status: "UNASSIGNED",
                isTemplate: true,
              };

              setChecklist(checklistFromTemplate);
              setPhotos([]);
              setSignatures({
                engineer: null,
                clientSiteEngineer: null,
                admin: null,
              });
            }
          } catch (templateErr) {
            console.log("Could not fetch template data:", templateErr.message);
            setChecklist(null);
          }
        } else {
          setChecklist(null);
          setPhotos([]);
          setSignatures({
            engineer: null,
            clientSiteEngineer: null,
            admin: null,
          });
        }
      }

      setLoading(false);
    } catch (err) {
      console.error("Error fetching data:", err);
      showToast("error", "Error", "Failed to load project data");
      setLoading(false);
    }
  };

  const fetchInstructionReplies = async (requestId) => {
    try {
      const userRole = getUserRole();
      const endpoint =
        userRole === "ENGINEER"
          ? `${url}/api/engineer/instruction-replies/${requestId}`
          : `${url}/api/admin/instruction-replies/${requestId}`;

      try {
        const response = await axios.get(endpoint, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (response.data?.data) {
          setSpecificInstructions(response.data.data);

          const initialReplies = {};
          response.data.data.forEach((inst, idx) => {
            initialReplies[idx] = "";
          });
          setNewReplies(initialReplies);
        }
      } catch (apiErr) {
        console.log(
          "Could not fetch instruction replies via API:",
          apiErr.message
        );
      }
    } catch (error) {
      console.error("Error in fetchInstructionReplies:", error);
    }
  };

  const handleAddInstructionReply = async (instructionIndex, reply) => {
    try {
      const userRole = getUserRole();
      const endpoint =
        userRole === "ENGINEER"
          ? `${url}/api/engineer/instruction-reply/${projectId}`
          : `${url}/api/admin/instruction-reply/${projectId}`;

      const response = await axios.post(
        endpoint,
        {
          instructionIndex,
          reply,
          userId: userData._id,
          userRole: userRole,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data?.data) {
        const newReply = response.data.data.reply;

        setSpecificInstructions((prev) => {
          const updated = [...prev];
          if (!updated[instructionIndex]) {
            updated[instructionIndex] = {
              index: instructionIndex,
              text: response.data.data.instructionText || "",
              replies: [newReply],
            };
          } else {
            updated[instructionIndex] = {
              ...updated[instructionIndex],
              replies: [...(updated[instructionIndex].replies || []), newReply],
            };
          }
          return updated;
        });

        showToast("success", "Success", "Reply added successfully");

        setNewReplies((prev) => ({
          ...prev,
          [instructionIndex]: "",
        }));

        setTimeout(() => {
          fetchInstructionReplies(projectId);
        }, 500);
      }
    } catch (err) {
      console.error("Error adding reply:", err);
      showToast(
        "error",
        "Error",
        err.response?.data?.message || "Failed to add reply"
      );
    }
  };

  const fetchUserDetails = async (userId, type) => {
    try {
      const response = await axios.get(`${url}/api/users/fetch`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { userID: userId },
      });

      if (response.data?.users?.length > 0) {
        const user = response.data.users[0];
        if (type === "client") {
          setClientDetails({
            name: user.name || user.companyName || "N/A",
            email: user.email || "-",
            phone: user.phone || "-",
          });
        }
      }
    } catch (err) {
      console.error(`Error fetching ${type} details:`, err);
      if (type === "client" && project) {
        setClientDetails({
          name: project.nameOfClient || "N/A",
          email: "-",
          phone: project.phoneOfClient || "-",
        });
      }
    }
  };

  const fetchAdminDetails = async () => {
    try {
      const response = await axios.get(`${url}/api/users/fetch`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          role: "ADMIN",
          status: "APPROVED",
          limit: 1
        },
      });

      if (response.data?.users?.length > 0) {
        const admin = response.data.users[0];

        // Use company details if available, otherwise fall back to admin details
        setAdminDetails({
          name: admin.name || "Admin",
          // Use company email and phone instead of admin's personal ones
          email: admin.companyDetails?.email || admin.email || "-",
          phone: admin.companyDetails?.phone || admin.phone || "-",
          // Store full company details if needed elsewhere
          companyDetails: admin.companyDetails || null,
        });
      } else {
        // Default values when no admin found
        setAdminDetails({
          name: "Admin",
          email: "-",
          phone: "-",
          companyDetails: null,
        });
      }
    } catch (err) {
      console.error("Error fetching admin details:", err);
      setAdminDetails({
        name: "Admin",
        email: "-",
        phone: "-",
        companyDetails: null,
      });
    }
  };

  const handleAssignEngineer = () => {
    setAssignModalOpen(true);
  };
  const handleAssignSuccess = useCallback((updatedProject) => {
    if (!isComponentMounted.current) return;

    setAssignModalOpen(false);
    //showToast('success', 'Success', 'Engineer assigned successfully');

    // Mark that changes were made
    setHasChanges(true);

    // CRITICAL FIX: Preserve all existing project data when updating
    setProject(prev => {
      if (!prev) return updatedProject;

      // Create a merged project object that preserves all existing data
      const mergedProject = {
        // First, keep all properties from the previous state
        ...prev,
        // Then override with updated fields
        assignedEngineer: updatedProject.assignedEngineer,
        status: updatedProject.status && updatedProject.status !== prev.status
          ? updatedProject.status
          : (originalProjectStatus || prev.status),
        assignedVisitDate: updatedProject.assignedVisitDate || prev.assignedVisitDate,

        // IMPORTANT: Explicitly preserve these critical fields
        // If they exist in updatedProject, use them; otherwise keep from prev
        preferredSiteVisitDate: updatedProject.preferredSiteVisitDate
          ? updatedProject.preferredSiteVisitDate
          : prev.preferredSiteVisitDate,

        additionalTime: updatedProject.additionalTime !== undefined
          ? updatedProject.additionalTime
          : prev.additionalTime,

        // Preserve other important fields
        nameOfProject: updatedProject.nameOfProject || prev.nameOfProject,
        detailedAddress: updatedProject.detailedAddress || prev.detailedAddress,
        nameOfClient: updatedProject.nameOfClient || prev.nameOfClient,
        phoneOfClient: updatedProject.phoneOfClient || prev.phoneOfClient,
        templateTitle: updatedProject.templateTitle || prev.templateTitle,
        specificInstructions: updatedProject.specificInstructions || prev.specificInstructions,
        adminRemarks: updatedProject.adminRemarks || prev.adminRemarks,
      };

      console.log("Merged project after engineer assignment:", {
        hasPreferredDate: !!mergedProject.preferredSiteVisitDate,
        hasAdditionalTime: mergedProject.additionalTime !== undefined,
        status: mergedProject.status,
        engineer: mergedProject.assignedEngineer?.name
      });

      return mergedProject;
    });
  }, [showToast, originalProjectStatus]);

  const fetchUserSignature = async () => {
    try {
      const userRole = getUserRole();

      // Only fetch for engineers and admins
      if (
        userRole === "ENGINEER" ||
        userRole === "ADMIN" ||
        userRole === "SUPERADMIN"
      ) {
        console.log(
          "Fetching signature for user:",
          userData._id,
          "Role:",
          userRole
        );
        const response = await axios.get(
          `${url}/api/users/signature/${userData._id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        console.log("Signature response:", response.data);

        if (response.data?.data) {
          if (userRole === "ENGINEER") {
            setUserSignature(response.data.data);
            console.log("Engineer signature set:", response.data.data);
          } else if (userRole === "ADMIN" || userRole === "SUPERADMIN") {
            setAdminSignature(response.data.data);
            console.log("Admin signature set:", response.data.data);
          }
        } else {
          console.log("No signature data found");
        }
      }
    } catch (err) {
      console.log("Could not fetch user signature:", err.message);
      // Not critical, so don't show error
    }
  };

  const handleRemarksChange = (sectionIndex, itemIndex, remarks) => {
    setChecklist((prev) => {
      if (!prev) return prev;

      return {
        ...prev,
        sections: prev.sections.map((section, sIdx) => {
          if (sIdx !== sectionIndex) return section;

          return {
            ...section,
            items: section.items.map((item, iIdx) =>
              iIdx === itemIndex ? { ...item, remarks } : item
            ),
          };
        }),
      };
    });
  };

  const handleSaveChecklist = async (status = "VISITED") => {
    if (!checklist) return;

    try {
      setSaving(true);

      const payload = {
        checklistId: checklist._id,
        sections: checklist.sections,
        status: status,
      };

      const response = await axios.put(
        `${url}/api/engineer/fill-checklist`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      showToast("success", "Success", "Checklist saved successfully");
      setChecklist(response.data.data);
      fetchData();
    } catch (err) {
      console.error("Error saving checklist:", err);
      showToast(
        "error",
        "Error",
        err.response?.data?.message || "Failed to save checklist"
      );
    } finally {
      setSaving(false);
    }
  };

  const handleSubmitChecklist = async () => {
    try {
      setSaving(true);

      const payload = {
        checklistId: checklist._id,
        sections: checklist.sections,
        status: "SUBMITTED",
        otherObservations: checklist.otherObservations || [],
      };

      const response = await axios.put(
        `${url}/api/engineer/fill-checklist`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      showToast("success", "Success", "Checklist submitted successfully");
      setChecklist(response.data.data);

      setTimeout(() => {
        onClose();
      }, 1000);
    } catch (err) {
      console.error("Error submitting checklist:", err);
      showToast(
        "error",
        "Error",
        err.response?.data?.message || "Failed to submit checklist"
      );
    } finally {
      setSaving(false);
    }
  };

  const handlePhotoUpload = async (photoData) => {
    try {
      const formData = new FormData();
      const filesArray = [photoData.file];

      filesArray.forEach((file, index) => {
        formData.append("photos", file);
      });

      formData.append("descriptions", photoData.title || "");

      const shapesArray = Array.isArray(photoData.shapes)
        ? photoData.shapes
        : [];
      formData.append("shapes", JSON.stringify(shapesArray));

      const response = await axios.post(
        `${url}/api/engineer/add-photos/${checklist._id}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      const processedPhotos = response.data.photos.map((photo) => ({
        ...photo,
        image: getFullImageUrl(photo.image),
      }));

      setPhotos(processedPhotos);
      setPhotoModalOpen(false);
      showToast("success", "Success", "Photo uploaded successfully");
    } catch (err) {
      console.error("Error uploading photo:", err);
      let errorMsg = "Failed to upload photo";
      if (err.response?.data?.message) {
        errorMsg = err.response.data.message;
      } else if (err.message) {
        errorMsg = err.message;
      }

      showToast("error", "Error", errorMsg);
    }
  };

  const handleSignatureSave = async (signatureData, role) => {
    try {
      const { signature, name } = signatureData;
      console.log("Saving signature for role:", role);

      // Extract base64 from data URL (format: data:image/png;base64,xxx)
      const base64Parts = signature.split(",");
      if (base64Parts.length !== 2) {
        throw new Error("Invalid signature format");
      }

      const byteString = atob(base64Parts[1]);
      const ab = new ArrayBuffer(byteString.length);
      const ia = new Uint8Array(ab);

      for (let i = 0; i < byteString.length; i++) {
        ia[i] = byteString.charCodeAt(i);
      }

      // Always use image/png for signatures
      const blob = new Blob([ab], { type: "image/png" });

      const formData = new FormData();
      formData.append("signature", blob, "signature.png");

      let apiEndpoint = "";

      if (hasRole("ENGINEER")) {
        apiEndpoint = `${url}/api/engineer/sign-checklist/${checklist._id}`;
        formData.append("role", role);

        if (role === "engineer" && userData?.name) {
          formData.append("name", userData.name);
        } else if (role === "clientSiteEngineer" && name) {
          formData.append("name", name);
        }
      } else if (hasRole("ADMIN") || hasRole("SUPERADMIN")) {
        apiEndpoint = `${url}/api/admin/checklist/sign/${checklist._id}`;
        formData.append("name", adminDetails?.name || "Admin");
      }

      console.log("Sending signature to:", apiEndpoint);
      const response = await axios.post(apiEndpoint, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
        timeout: 30000,
      });

      // Save user signature to database if this is engineer or admin
      if (role === "engineer" || role === "admin") {
        try {
          const userSignatureFormData = new FormData();
          userSignatureFormData.append("signature", blob, "signature.png");

          const userId = userData._id;
          await axios.post(
            `${url}/api/users/signature/${userId}`,
            userSignatureFormData,
            {
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "multipart/form-data",
              },
            }
          );

          // Update local state with saved signature
          const signatureResponse = await axios.get(
            `${url}/api/users/signature/${userId}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );

          if (signatureResponse.data?.data) {
            if (role === "engineer") {
              setUserSignature(signatureResponse.data.data);
            } else if (role === "admin") {
              setAdminSignature(signatureResponse.data.data);
            }
          }
        } catch (signatureErr) {
          console.error("Error saving user signature:", signatureErr);
        }
      }

      // Update local state WITHOUT refreshing whole page
      const responseSignatures =
        response.data.signatures || response.data.data?.signatures;
      if (responseSignatures) {
        const processedSignatures = {
          engineer: responseSignatures.engineer
            ? {
              ...responseSignatures.engineer,
              signature: getFullImageUrl(
                responseSignatures.engineer.signature
              ),
            }
            : null,
          clientSiteEngineer: responseSignatures.clientSiteEngineer
            ? {
              ...responseSignatures.clientSiteEngineer,
              signature: getFullImageUrl(
                responseSignatures.clientSiteEngineer.signature
              ),
            }
            : null,
          admin: responseSignatures.admin
            ? {
              ...responseSignatures.admin,
              signature: getFullImageUrl(responseSignatures.admin.signature),
            }
            : null,
        };
        setSignatures(processedSignatures);

        // Update checklist status locally if needed
        if (checklist && response.data.data) {
          setChecklist((prev) => ({
            ...prev,
            status: response.data.data.status || prev.status,
            signatures: processedSignatures,
          }));
        }
      }

      setSignatureModalOpen(false);
      setCurrentSignatureRole(null);
      showToast(
        "success",
        "Success",
        response.data.message || "Signature saved successfully"
      );
    } catch (err) {
      console.error("Error saving signature:", err);
      showToast(
        "error",
        "Error",
        err.response?.data?.message || "Failed to save signature"
      );
    }
  };

  const handleAddObservation = async (observationsArray) => {
    try {
      const payload = {
        checklistId: checklist._id,
        sections: checklist.sections,
        otherObservations: observationsArray,
        status: checklist.status || "VISITED",
      };

      const response = await axios.put(
        `${url}/api/engineer/fill-checklist`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data?.data) {
        setChecklist((prev) => ({
          ...prev,
          otherObservations:
            response.data.data.otherObservations || observationsArray,
          sections: response.data.data.sections || checklist.sections,
        }));
      }

      showToast("success", "Success", "Observations updated successfully");
    } catch (err) {
      console.error("Error adding observations:", err);
      showToast(
        "error",
        "Error",
        err.response?.data?.message || "Failed to update observations"
      );
    }
  };

  const handleDeleteObservation = async (observationsArray) => {
    try {
      const payload = {
        checklistId: checklist._id,
        sections: checklist.sections,
        otherObservations: observationsArray,
        status: checklist.status || "VISITED",
      };

      const response = await axios.put(
        `${url}/api/engineer/fill-checklist`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data?.data) {
        setChecklist((prev) => ({
          ...prev,
          otherObservations:
            response.data.data.otherObservations || observationsArray,
          sections: response.data.data.sections || checklist.sections,
        }));
      }

      showToast("success", "Success", "Observations updated successfully");
    } catch (err) {
      console.error("Error deleting observations:", err);
      showToast(
        "error",
        "Error",
        err.response?.data?.message || "Failed to update observations"
      );
    }
  };

  const handleAddAdminRemarks = async (remarksArray) => {
    try {
      const formattedRemarks = remarksArray;

      const response = await axios.post(
        `${url}/api/admin/checklist/remarks/${projectId}`,
        { remarks: formattedRemarks },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      showToast("success", "Success", "Admin remarks saved successfully");

      setProject((prev) => ({
        ...prev,
        adminRemarks: formattedRemarks,
      }));
    } catch (err) {
      console.error("Error saving admin remarks:", err);
      showToast(
        "error",
        "Error",
        err.response?.data?.message || "Failed to save admin remarks"
      );
    }
  };
  const handleDeleteReply = async (instructionIndex, replyIndex) => {
    try {
      // Use engineer endpoint for both roles
      const endpoint = `${url}/api/engineer/instruction-reply/${projectId}`;

      const response = await axios.delete(
        endpoint,
        {
          headers: { Authorization: `Bearer ${token}` },
          data: {
            instructionIndex,
            replyIndex,
            userId: userData._id
          }
        }
      );

      if (response.data?.data) {
        // Update local state immediately
        setSpecificInstructions(prev => {
          const updated = [...prev];
          if (updated[instructionIndex]?.replies) {
            updated[instructionIndex].replies = response.data.data.remainingReplies || [];
          }
          return updated;
        });

        showToast('success', 'Success', 'Reply deleted successfully');
      }
    } catch (err) {
      console.error('Error deleting reply:', err);
      showToast(
        'error',
        'Error',
        err.response?.data?.message || 'Failed to delete reply'
      );
    }
  };
  const getAssignedEngineerName = () => {
    const engineerDetails = getEngineerDetails();
    return engineerDetails?.name || "Engineer";
  };

  // UPDATED: Get POC name
  const getPOCDetails = () => {
    return {
      name: project?.nameOfClient || "Point of Contact Person",
      email: project?.pocEmail || "-",
      phone: project?.phoneOfClient || "-",
    };
  };

  // UPDATED: Handle add signature with check for existing signature
  const handleAddSignature = (role) => {
    setCurrentSignatureRole(role);
    setSignatureModalOpen(true);
  };

  const handleOpenDrawing = (drawingData) => {
    if (!drawingData?.drawing) {
      showToast("error", "Error", "No drawing available");
      return;
    }

    const fullDrawingUrl = getFullImageUrl(drawingData.drawing);
    setSelectedDrawing({
      ...drawingData,
      fullUrl: fullDrawingUrl,
    });
    setDrawingModalOpen(true);
  };

  const handleCloseDrawing = () => {
    setDrawingModalOpen(false);
    setSelectedDrawing(null);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
    }).replace(/\//g, '/')
  }

  const formatDateTime = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    const dateStr = date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
    }).replace(/\//g, '/');
    const timeStr = date.toLocaleTimeString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
    });
    return `${dateStr} | ${timeStr}`;
  };

  const handleAnswerChange = (sectionIndex, itemIndex, answer) => {
    setChecklist((prev) => {
      if (!prev) return prev;

      return {
        ...prev,
        sections: prev.sections.map((section, sIdx) => {
          if (sIdx !== sectionIndex) return section;

          return {
            ...section,
            items: section.items.map((item, iIdx) =>
              iIdx === itemIndex ? { ...item, answer } : item
            ),
          };
        }),
      };
    });
  };
  const getEngineerDetails = () => {
    if (!project?.assignedEngineer) {
      return {
        name: "Not Assigned",
        email: "-",
        phone: "-",
      };
    }

    if (typeof project.assignedEngineer === "object") {
      return {
        name: project.assignedEngineer.name || "Engineer",
        email: project.assignedEngineer.email || "-",
        phone: project.assignedEngineer.phone || "-",
      };
    }

    return {
      name: "Engineer",
      email: "-",
      phone: "-",
    };
  };

  const getThirdColumnInfo = () => {
    const userRole = getUserRole();
    const projectStatus = project?.status;

    if (projectStatus === "UNASSIGNED") {
      return null;
    }

    if (
      userRole === "CLIENT" &&
      projectStatus &&
      projectStatus !== "UNASSIGNED"
    ) {
      const engineerDetails = getEngineerDetails();
      return {
        title: "Assigned Engineer",
        data: {
          name: engineerDetails.name,
          // Use company email and phone from adminDetails
          email: adminDetails?.companyDetails?.email || adminDetails?.email || "-",
          phone: adminDetails?.companyDetails?.phone || adminDetails?.phone || "-",
        },
      };
    }

    if (["ADMIN", "SUPERADMIN", "ENGINEER"].includes(userRole)) {
      const engineerDetails = getEngineerDetails();
      return {
        title: "Assigned Engineer",
        data: {
          name: engineerDetails.name,
          // Use company email and phone for admin/engineer view too
          email: adminDetails?.companyDetails?.email || adminDetails?.email || engineerDetails.email || "-",
          phone: adminDetails?.companyDetails?.phone || adminDetails?.phone || engineerDetails.phone || "-",
        },
      };
    }

    return null;
  };

  if (loading) {
    return (
      <Box
        sx={{
          width: 800,
          height: "100vh",
          position: "fixed",
          top: 0,
          right: open ? 0 : "-100%",
          bgcolor: "#fff",
          zIndex: 1300,
          transition: "right 0.3s ease",
          boxShadow: "-4px 0 12px rgba(0,0,0,0.1)",
          p: 3,
          overflowY: "auto",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const thirdColumnInfo = getThirdColumnInfo();
  const pocName = getPOCDetails().name;

  return (
    <>
      {open && (
        <Box
          onClick={onClose}
          sx={{
            position: "fixed",
            inset: 0,
            bgcolor: "rgba(0,0,0,0.5)",
            zIndex: 1299,
          }}
        />
      )}

      <Box
        sx={{
          width: 800,
          height: "100vh",
          position: "fixed",
          top: 0,
          right: open ? 0 : "-100%",
          bgcolor: "#fff",
          zIndex: 1300,
          transition: "right 0.3s ease",
          boxShadow: "-4px 0 12px rgba(0,0,0,0.1)",
          p: 3,
          overflowY: "auto",
          pb: 12,
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
          <Typography sx={{ ...TYPOGRAPHY.H2, color: COLORS.PRIMARY }}>
            {mode === "edit" ? "Edit Checklist" : "View Checklist"}
          </Typography>
          <Box sx={{ display: "flex", gap: 2 }}>
            <IconButton onClick={onClose} sx={{ p: 0.5 }}>
              <Icon name="close" color={COLORS.ERROR} />
            </IconButton>
          </Box>
        </Box>

        {/* NEW: Show date restriction alert if applicable */}
        {/* {renderDateAlert()} */}

        {!project ? (
          <Typography sx={{ color: COLORS.TEXT, textAlign: "center" }}>
            No project data available
          </Typography>
        ) : (
          <>
            <Box sx={{ mb: 2 }}>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr auto",
                  rowGap: 1,
                  alignItems: "center",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: 1,
                  }}
                >
                  <Icon name="block" color={COLORS.PRIMARY} size={20} />
                  <Typography
                    sx={{ ...TYPOGRAPHY.H3, wordBreak: "break-word" }}
                  >
                    {project.nameOfProject}
                  </Typography>
                  <Chip
                    label={project.status}
                    size="small"
                    sx={{
                      ml: 2,
                      bgcolor: STATUS_COLORS[project.status] || "#7ED957",
                      color: "#fff",
                      ...TYPOGRAPHY.BODY_SMALL,
                    }}
                  />
                </Box>

                <Typography
                  sx={{
                    ...TYPOGRAPHY.BODY_LARGE,
                    color: COLORS.TEXT,
                    textAlign: "right",
                    whiteSpace: "nowrap",
                  }}
                >
                  Entry on: {formatDate(project.requestedDate)}
                </Typography>

                <Typography
                  sx={{
                    ...TYPOGRAPHY.BODY_STANDARD,
                    color: COLORS.SECONDARY,
                    wordBreak: "break-word",
                  }}
                >
                  {project.detailedAddress}
                </Typography>

                {project.assignedVisitDate && (
                  <Typography
                    sx={{
                      ...TYPOGRAPHY.BODY_LARGE,
                      color: COLORS.SECONDARY,
                      textAlign: "right",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-end",
                      gap: 0.5,
                      whiteSpace: "nowrap",
                    }}
                  >
                    Visit Scheduled
                    <Icon name="calendar3" />
                    {formatDateTime(project.assignedVisitDate)}
                  </Typography>
                )}
              </Box>
            </Box>

            <DividerLine />

            <StatusProgress
              currentStatus={project.status}
              userRole={userRole}
            />

            {/* In your main component's render */}
            <div onClick={(e) => e.stopPropagation()}>
              <ProjectMetaRow
                project={project}
                role={userRole}
                onAssignEngineer={handleAssignEngineer}
                onDateUpdate={handleDateUpdate}
                onAdditionalTimeUpdate={handleAdditionalTimeUpdate}
                editable={mode === "edit"}
                onOpenDrawing={handleOpenDrawing}
              />
            </div>
            <DividerLine />

            <Box
              sx={{
                display: "flex",
                justifyContent: thirdColumnInfo
                  ? "space-between"
                  : "flex-start",
                mt: 3,
                mb: 3,
                gap: 2,
              }}
            >
              <InfoColumn
                title="Client Details"
                data={
                  clientDetails || {
                    name: project?.nameOfClient || "N/A",
                    email: "-",
                    phone: project?.phoneOfClient || "-",
                  }
                }
              />

              <InfoColumn title="POC at Project" data={getPOCDetails()} />

              {thirdColumnInfo && (
                <InfoColumn
                  title={thirdColumnInfo.title}
                  data={thirdColumnInfo.data}
                />
              )}
            </Box>

            <DividerLine />

            {specificInstructions.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography sx={{ ...TYPOGRAPHY.H4, color: "#000", mb: 2 }}>
                  Special Instruction
                </Typography>

                {specificInstructions.map((instruction, idx) => {
                  if (!instruction) return null;

                  const instructionText = instruction.text || "";
                  const replies = instruction.replies || [];

                  // UPDATED: Use canEditChecklist for permission check
                  const canDeleteReplies = canEditChecklist &&
                    (userRole === "ENGINEER" || userRole === "ADMIN" || userRole === "SUPERADMIN");

                  return (
                    <Box key={idx} sx={{ mb: 2 }}>
                      <Typography sx={{ ...TYPOGRAPHY.H4, color: "#000", mb: 0.5 }}>
                        {idx + 1}. {instructionText}
                      </Typography>

                      {/* UPDATED: Use canEditChecklist instead of mode === "edit" */}
                      {canEditChecklist &&
                        (userRole === "ENGINEER" || userRole === "ADMIN" || userRole === "SUPERADMIN") && (
                          <Box sx={{ mb: 1, ml: 2 }}>
                            <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                              <TextField
                                fullWidth
                                multiline
                                minRows={1}
                                maxRows={6}
                                placeholder="Type your reply..."
                                value={newReplies[idx] || ""}
                                onChange={(e) =>
                                  setNewReplies((prev) => ({
                                    ...prev,
                                    [idx]: e.target.value,
                                  }))
                                }
                                onKeyDown={(e) => {
                                  if (e.key === "Enter" && !e.shiftKey) {
                                    e.preventDefault();
                                    if (newReplies[idx]?.trim()) {
                                      handleAddInstructionReply(idx, newReplies[idx]);
                                      setNewReplies((prev) => ({
                                        ...prev,
                                        [idx]: "",
                                      }));
                                    }
                                  }
                                }}
                                size="small"
                                sx={{
                                  "& .MuiOutlinedInput-root": {
                                    minHeight: "40px",
                                    alignItems: "flex-start",
                                  },
                                  "& .MuiInputBase-input": {
                                    padding: "4px 8px",
                                    lineHeight: "15px",
                                  },
                                }}
                              />

                              <Button
                                variant="contained"
                                onClick={() => {
                                  if (newReplies[idx]?.trim()) {
                                    handleAddInstructionReply(idx, newReplies[idx]);
                                    setNewReplies((prev) => ({
                                      ...prev,
                                      [idx]: "",
                                    }));
                                  }
                                }}
                                disabled={!newReplies[idx]?.trim()}
                                sx={{
                                  bgcolor: COLORS.SECONDARY,
                                  height: "40px",
                                  minWidth: 90,
                                  whiteSpace: "nowrap",
                                }}
                              >
                                Add
                              </Button>
                            </Box>
                          </Box>
                        )}

                      <Box sx={{ ml: 2, display: "flex", flexDirection: "column", gap: 1 }}>
                        {replies.length > 0 ? (
                          replies.map((reply, replyIdx) => {
                            // Check if current user is the author of this reply
                            const isCurrentUserAuthor =
                              reply.repliedBy?._id === userData?._id ||
                              reply.repliedBy === userData?._id;

                            return (
                              <Paper
                                key={replyIdx}
                                variant="outlined"
                                sx={{
                                  minHeight: "40px",
                                  px: 1.5,
                                  py: 0.5,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  backgroundColor: "#fff",
                                  position: "relative",
                                  "&:hover": {
                                    backgroundColor: "#f9f9f9",
                                    boxShadow: "0px 2px 4px rgba(0,0,0,0.05)",
                                  },
                                  transition: "all 0.2s ease",
                                }}
                              >
                                <Box sx={{
                                  flex: 1,
                                  pr: canDeleteReplies && isCurrentUserAuthor ? 4 : 1
                                }}>
                                  <Typography
                                    sx={{
                                      color: COLORS.PRIMARY,
                                      whiteSpace: "normal",
                                      wordBreak: "break-word",
                                      fontSize: "14px",
                                      lineHeight: 1.5,
                                    }}
                                  >
                                    {reply.content}
                                  </Typography>

                                  {/* Reply metadata - Commented out */}
                                  {/* 
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
                        <Typography sx={{ 
                          fontSize: "12px", 
                          color: COLORS.SECONDARY, 
                          fontStyle: "italic" 
                        }}>
                          By: {reply.repliedBy?.name || reply.userName || "User"}
                        </Typography>
                        
                        <Typography sx={{ fontSize: "12px", color: COLORS.TEXT }}>
                          •
                        </Typography>
                        
                        <Typography sx={{ fontSize: "12px", color: COLORS.TEXT }}>
                          {reply.createdAt 
                            ? new Date(reply.createdAt).toLocaleString('en-IN', {
                                day: 'numeric',
                                month: 'short',
                                hour: '2-digit',
                                minute: '2-digit'
                              })
                            : "Just now"}
                        </Typography>
                      </Box>
                      */}
                                </Box>

                                {/* UPDATED: Delete icon - only show if canDeleteReplies AND isCurrentUserAuthor */}
                                {canDeleteReplies && isCurrentUserAuthor && (
                                  <Box sx={{ position: "absolute", top: 1, right: 3, zIndex: 2 }}>
                                    <IconButton
                                      onClick={() => handleDeleteReply(idx, replyIdx)}
                                      size="small"
                                      sx={{ color: COLORS.ERROR }}
                                      title="Delete reply"
                                    >
                                      <Icon name="close2" />
                                    </IconButton>
                                  </Box>
                                )}
                              </Paper>
                            );
                          })
                        ) : (
                          <Typography sx={{
                            fontSize: 14,
                            color: COLORS.TEXT,
                            fontStyle: "italic"
                          }}>
                            No replies yet
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            )}

            {checklist ? (
              <>
                <Box sx={{ mb: 2 }}>
                  <Typography sx={{ ...TYPOGRAPHY.H4, color: "#000", mb: 2 }}>
                    Checklist: {checklist.title}
                  </Typography>
                  {checklist.description && (
                    <Typography
                      sx={{
                        ...TYPOGRAPHY.BODY_STANDARD,
                        color: COLORS.SECONDARY,
                        mb: 2,
                      }}
                    >
                      {checklist.description}
                    </Typography>
                  )}
                  {/* UPDATED: Use canEditChecklist for edit permission */}
                  <ChecklistTable
                    checklistData={checklist}
                    editable={canEditChecklist}
                    onAnswerChange={handleAnswerChange}
                    onRemarksChange={handleRemarksChange}
                  />
                </Box>

                {/* UPDATED: Use canEditChecklist for edit permission */}
                <OtherObservations
                  observations={checklist.otherObservations || []}
                  onAddObservation={handleAddObservation}
                  onDeleteObservation={handleDeleteObservation}
                  editable={canEditChecklist}
                />

                {/* UPDATED: Use canEditChecklist for edit permission */}
                <PhotoGallery
                  photos={photos}
                  onDeletePhoto={handleDeletePhoto}
                  onAddPhoto={() => canEditChecklist ? setPhotoModalOpen(true) : null}
                  editable={canEditChecklist}
                />

                {/* UPDATED: Use canEditChecklist for edit permission */}
                <AdminRemarks
                  remarks={project.adminRemarks}
                  onAddRemarks={handleAddAdminRemarks}
                  editable={canEditChecklist}
                  onDeleteRemark={handleDeleteAdminRemark}
                  userRole={userRole}
                />

                {/* UPDATED: Use canEditChecklist for edit permission */}
                <SignaturesDisplay
                  signatures={signatures}
                  onAddSignature={canEditChecklist ? handleAddSignature : null}
                  onApplyStoredSignature={canEditChecklist ? handleApplyStoredSignature : null}
                  editable={canEditChecklist}
                  userRole={userRole}
                  pocName={getPOCDetails().name}
                  assignedEngineerName={getAssignedEngineerName()}
                  adminDetails={adminDetails}
                  userData={userData}
                  userSignature={userSignature}
                  adminSignature={adminSignature}
                />
              </>
            ) : (
              <Typography
                sx={{ color: COLORS.TEXT, textAlign: "center", py: 3 }}
              >
                No checklist data available
              </Typography>
            )}

            {hasRole("CLIENT") &&
              generalContent.thankYou?.isActive &&
              generalContent.thankYou?.content && (
                <Typography
                  sx={{
                    ...TYPOGRAPHY.BODY_STANDARD,
                    textAlign: "center",
                    mt: 4,
                    fontStyle: "italic",
                    color: COLORS.PRIMARY,
                  }}
                >
                  {generalContent.thankYou.content}
                </Typography>
              )}

            {hasRole("CLIENT") &&
              generalContent.termsConditions?.isActive &&
              generalContent.termsConditions?.content && (
                <Box sx={{ mt: 4, pageBreakBefore: "always" }}>
                  <Typography sx={{ ...TYPOGRAPHY.H4, color: "#000", mb: 1 }}>
                    {generalContent.termsConditions.title ||
                      "Terms & Conditions"}
                  </Typography>
                  <Typography
                    sx={{
                      ...TYPOGRAPHY.BODY_STANDARD,
                      color: COLORS.PRIMARY,
                      whiteSpace: "pre-line",
                      lineHeight: 1.6,
                      pl: "20px",
                    }}
                  >
                    {generalContent.termsConditions.content}
                  </Typography>
                </Box>
              )}
          </>
        )}

        {/* UPDATED: Only show submit button if canEditChecklist */}
        {canEditChecklist && checklist && (
          <Box
            sx={{
              position: "fixed",
              bottom: 0,
              right: 0,
              width: 800,
              backgroundColor: "#fff",
              py: 2,
              px: 3,
              borderTop: `1px solid ${COLORS.LIGHT_SECONDARY}`,
              display: "flex",
              justifyContent: "flex-end",
              zIndex: 1301,
              boxShadow: "0px -2px 10px rgba(0,0,0,0.1)",
            }}
          >
            <Button
              variant="contained"
              onClick={handleSubmitChecklist}
              disabled={saving}
              startIcon={<Icon name="check" />}
              sx={{
                bgcolor: COLORS.SECONDARY,
                color: "#fff",
                px: 3,
                py: 1.2,
                borderRadius: 2,
                fontWeight: 600,
                textTransform: "none",
                minWidth: 180,
                "&:hover": {
                  bgcolor: COLORS.SECONDARY,
                },
                "&.Mui-disabled": {
                  bgcolor: COLORS.LIGHT_SECONDARY,
                },
              }}
            >
              {saving ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                "Submit Checklist"
              )}
            </Button>
          </Box>
        )}

        {/* Modals */}
        {/* UPDATED: Only open photo modal if canEditChecklist */}
        {photoModalOpen && canEditChecklist && (
          <PhotoUploadWithDrawing
            open={photoModalOpen}
            onClose={() => setPhotoModalOpen(false)}
            onSave={handlePhotoUpload}
            maxPhotos={6 - photos.length}
          />
        )}

        {/* UPDATED: Only open signature modal if canEditChecklist */}
        {signatureModalOpen && canEditChecklist && (
          <SignatureModal
            open={signatureModalOpen}
            onClose={() => {
              setSignatureModalOpen(false);
              setCurrentSignatureRole(null);
            }}
            onSave={(signatureData) =>
              handleSignatureSave(signatureData, currentSignatureRole)
            }
            role={currentSignatureRole}
            userData={userData}
            pocName={getPOCDetails().name}
            assignedEngineerName={getEngineerDetails().name || "Engineer"}
          />
        )}

        {/* Assign Engineer Modal */}
        {project && (hasRole("ADMIN") || hasRole("SUPERADMIN")) && (
          <AssignEngineerModal
            open={assignModalOpen}
            onClose={() => setAssignModalOpen(false)}
            project={project}
            onSuccess={handleAssignSuccess}
          />
        )}

        {/* Drawing Viewer Modal */}
        <DrawingViewerModal
          open={drawingModalOpen}
          onClose={handleCloseDrawing}
          drawing={selectedDrawing}
        />
      </Box>
    </>
  );
};

// Wrap the main component with ToastProvider
const EditChecklistSidebarWithToast = (props) => {
  return (
    <ToastProvider>
      <EditChecklistSidebar {...props} />
    </ToastProvider>
  );
};

export default EditChecklistSidebarWithToast;